<script setup lang="ts">
import type { VxeGridProps } from '#/adapter/vxe-table';
import type {
  AuthorizedOrderImportFailRow,
  AuthorizedOrderImportRow,
  AuthorizedOrderWaybillParam,
  ShippingServiceOption,
} from '#/api/authorized-order';
import type {
  AuthorizedOrderDetailVo,
  AuthorizedOrderItemVo,
  AuthorizedOrderLinkSummaryVo,
  AuthorizedOrderListVo,
  AuthorizedOrderProcessStatus,
  AuthorizedOrderSyncTaskVo,
} from '#/api/authorized-order/model';

import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useVbenDrawer } from '@vben/common-ui';

import {
  ElBadge,
  ElButton,
  ElDialog,
  ElDrawer,
  ElEmpty,
  ElForm,
  ElFormItem,
  ElImage,
  ElInput,
  ElInputNumber,
  ElMessage,
  ElMessageBox,
  ElOption,
  ElPagination,
  ElSelect,
  ElTable,
  ElTableColumn,
  ElTabPane,
  ElTabs,
  ElTag,
  ElTooltip,
} from 'element-plus';

import { useVbenVxeGrid } from '#/adapter/vxe-table';
import {
  batchSaveAuthorizedOrderColorTag,
  buyAuthorizedOrderWaybill,
  deleteAuthorizedOrder,
  downloadAuthorizedOrderTemplate,
  getAuthorizedOrderDetail,
  getAuthorizedOrderSyncTask,
  importAuthorizedOrderCheck,
  importAuthorizedOrderDo,
  linkAuthorizedOrderInbound,
  linkAuthorizedOrderOutbound,
  linkAuthorizedOrderPreOnline,
  listAuthorizedOrders,
  listWaybillShippingServices,
  retryAuthorizedOrderWaybill,
  saveAuthorizedOrderColorTag,
  saveAuthorizedOrderItemPurchaseInfo,
  statsAuthorizedOrders,
  syncAuthorizedOrders,
  uploadManualAuthorizedOrderWaybill,
} from '#/api/authorized-order';
import { getShopListApi } from '#/api/core/auth';
import { warehouseListApi } from '#/api/core/goods';
import InboundOrderCreateSplitDialog from '#/views/workspace/inbound-order/components/InboundOrderCreateSplitDialog.vue';
import InboundOrderDetailDialog from '#/views/workspace/inbound-order/components/InboundOrderDetailDialog.vue';
import OutboundOrderCreateDialog from '#/views/workspace/outbound-order/components/OutboundOrderCreateDialog.vue';
import PreOnlineOrderCreateDialog from '#/views/workspace/pre-online/components/PreOnlineOrderCreateDialog.vue';
import PreOnlineOrderDetailDialog from '#/views/workspace/pre-online/components/PreOnlineOrderDetailDialog.vue';
import PurchaseAdd from '#/views/workspace/purchase-order/components/PurchaseAdd.vue';

import {
  dash,
  formatAuthorizedOrderMoney,
  formatDateTime,
  isApiAuthorizedOrder,
  PLATFORM_OPTIONS,
  processStatusLabel,
} from './columns';

function financeMoney(
  row: undefined | { financeCurrency?: string },
  value?: null | number | string,
  forceNegative = false,
) {
  return formatAuthorizedOrderMoney(
    value,
    row?.financeCurrency || 'USD',
    forceNegative,
  );
}

type StatusTab = {
  count?: number;
  externalStatusMapped?: string;
  label: string;
  platformCode?: string;
  processStatus?: AuthorizedOrderProcessStatus;
  value: string;
};

const STATUS_TABS: StatusTab[] = [
  { label: '全部', value: '' },
  { externalStatusMapped: '处理中', label: '处理中', value: 'ON_HOLD' },
  { externalStatusMapped: '待发货', label: '待发货', value: 'WAIT_SHIP' },
  { externalStatusMapped: '部分发货', label: '部分发货', value: 'PART_SHIP' },
  { externalStatusMapped: '待揽收', label: '待揽收', value: 'WAIT_PICKUP' },
  { externalStatusMapped: '运输中', label: '运输中', value: 'IN_TRANSIT' },
  { externalStatusMapped: '已送达', label: '已送达', value: 'DELIVERED' },
  { externalStatusMapped: '已完成', label: '已完成', value: 'COMPLETED' },
  { processStatus: 'CANCELED', label: '已取消', value: 'CANCELED' },
  { label: '手工订单', platformCode: 'MANUAL', value: 'MANUAL' },
];

const WAYBILL_OPTIONS = [
  { label: '全部面单状态', value: '' },
  { label: '未购买', value: 'NONE' },
  { label: '已购买', value: 'GENERATED' },
  { label: '面单获取失败', value: 'UPLOAD_FAILED' },
  { label: '已上传', value: 'UPLOADED' },
];
const ON_HOLD_BLOCK_REASON =
  '订单仍处于TikTok ON_HOLD阶段，平台暂未开放收件地址，暂不能履约';

function isOnHold(row?: { externalStatusRaw?: string }) {
  return row?.externalStatusRaw === 'ON_HOLD';
}
const NO_COLOR_TAG = '__NONE__';
const COLOR_TAG_OPTIONS = [
  { color: 'transparent', label: '无标签', value: NO_COLOR_TAG },
  { color: '#f56c6c', label: '红', value: 'red' },
  { color: '#e6a23c', label: '橙', value: 'orange' },
  { color: '#f7d154', label: '黄', value: 'yellow' },
  { color: '#67c23a', label: '绿', value: 'green' },
  { color: '#14b8a6', label: '青', value: 'cyan' },
  { color: '#409eff', label: '蓝', value: 'blue' },
  { color: '#8b5cf6', label: '紫', value: 'purple' },
  { color: '#909399', label: '灰', value: 'gray' },
] as const;
const COLOR_TAG_FILTER_OPTIONS = [
  { color: 'transparent', label: '全部标签', value: '' },
  ...COLOR_TAG_OPTIONS,
] as const;

const activeTab = ref('');
const data = ref<AuthorizedOrderListVo[]>([]);
// tab 各状态计数：后端 /stats 一次性返回 { total, WAIT_SHIP, PART_SHIP, ... }
// 后端剔除了 externalStatusMapped / processStatus 维度筛选，所以切 tab 数字稳定
const statsMap = ref<Record<string, number>>({});
// TK 仓库列表缓存：tiktokShopId → warehouseId → 仓库地址信息
// 列表渲染地址列时反查发货仓地址，避免每行单独调 TK 接口
type WarehouseEntry = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  contactPerson?: string;
  fullAddress?: string;
  isDefault?: boolean;
  name?: string;
  phoneNumber?: string;
  postalCode?: string;
  state?: string;
};
const warehouseCache = ref<Record<string, Record<string, WarehouseEntry>>>({});
const total = ref(0);
const pageNum = ref(1);
const pageSize = ref(20);
const keyword = ref('');
const platformCode = ref('');
const shopKeyword = ref('');
const colorTag = ref('');
const batchColorTag = ref(NO_COLOR_TAG);
const waybillFileStatus = ref('');
// 关联采购/入库/出库/预上网状态筛选：'' 全部 / true 是 / false 否
const hasPurchase = ref<'' | boolean>('');
const hasInbound = ref<'' | boolean>('');
const hasOutbound = ref<'' | boolean>('');
const hasPreOnline = ref<'' | boolean>('');
const loading = ref(false);
const syncing = ref(false);
const syncProgress = ref(0);
const syncProgressText = ref('');
const syncDialogOpen = ref(false);
const syncShopLoading = ref(false);
const selectedSyncShopIds = ref<number[]>([]);
const syncShopOptions = ref<
  Array<{
    authAppProfile?: string;
    label: string;
    region?: string;
    sellerType?: string;
    value: number;
  }>
>([]);

function formatSyncShopType(sellerType?: string) {
  if (sellerType === 'LOCAL') return '美国ACCU店铺';
  if (sellerType === 'CROSS_BORDER') return '美国跨境POP店铺';
  return sellerType || '未知类型';
}
let syncProgressTimer: number | undefined;
const selectedIds = ref<number[]>([]);
const drawerOpen = ref(false);
const currentRow = ref<AuthorizedOrderDetailVo>();
const purchaseDialogOpen = ref(false);
const purchaseDialogMode = ref<'edit' | 'view'>('view');
const purchaseDialogRow = ref<AuthorizedOrderDetailVo>();
const purchaseDialogLoading = ref(false);
const purchaseDialogSubmitting = ref(false);
const waybillDialogOpen = ref(false);
const waybillDialogUrl = ref('');
const waybillDialogNo = ref('');
// Excel 导入授权订单（T3，两阶段：选择文件 → 自动 check → 预览成功/失败 → 提交落库）
const importDialogOpen = ref(false);
const importCheckLoading = ref(false);
const importSubmitting = ref(false);
const importFile = ref<File | null>(null);
const importTotalRows = ref(0);
const importSuccessRows = ref<AuthorizedOrderImportRow[]>([]);
const importFailRows = ref<AuthorizedOrderImportFailRow[]>([]);
const importActiveTab = ref<'fail' | 'success'>('success');
const importFinalResult = ref<null | {
  createdCount: number;
  groupCount: number;
  skippedExistsCount: number;
}>(null);
const importFileInputRef = ref<HTMLInputElement | null>(null);
function triggerImportFilePick() {
  importFileInputRef.value?.click();
}

// 跟采购订单导入对齐：vxe-grid 渲染识别成功/失败两个表格
type ImportRowType = Record<string, any>;
const importSuccessGridOptions: VxeGridProps<ImportRowType> = {
  autoResize: true,
  height: 'auto',
  columns: [
    { title: '行号', field: 'rowNum', width: 70, align: 'center' },
    {
      title: '经营平台订单号',
      field: 'platformOrderNo',
      minWidth: 150,
      showOverflow: 'tooltip',
    },
    {
      title: '店铺',
      field: 'shopName',
      minWidth: 120,
      showOverflow: 'tooltip',
    },
    {
      title: '付款时间',
      field: 'externalPaidAt',
      minWidth: 150,
      showOverflow: 'tooltip',
    },
    {
      title: '截止发货',
      field: 'externalShipDeadlineAt',
      minWidth: 150,
      showOverflow: 'tooltip',
    },
    { title: '买家', field: 'buyerName', minWidth: 100 },
    { title: '收件人', field: 'recipientName', minWidth: 100 },
    { title: 'SKU', field: 'sku', minWidth: 130, showOverflow: 'tooltip' },
    {
      title: '商品名称',
      field: 'productName',
      minWidth: 160,
      showOverflow: 'tooltip',
    },
    { title: '规格', field: 'spec', minWidth: 100 },
    { title: '数量', field: 'quantity', width: 70, align: 'center' },
    { title: '单价', field: 'unitPrice', width: 90, align: 'right' },
    { title: '币种', field: 'currency', width: 70, align: 'center' },
    {
      title: '商品图片',
      field: 'imageUrl',
      minWidth: 120,
      showOverflow: 'tooltip',
    },
    {
      title: '采购链接',
      field: 'purchaseLink',
      minWidth: 140,
      showOverflow: 'tooltip',
    },
    {
      title: '面单号',
      field: 'waybillNo',
      minWidth: 120,
      showOverflow: 'tooltip',
    },
  ],
  data: [],
  keepSource: false,
};
const importFailGridOptions: VxeGridProps<ImportRowType> = {
  autoResize: true,
  height: 'auto',
  showOverflow: false,
  rowConfig: { height: 56 },
  columns: [
    { title: '行号', field: 'rowNum', width: 70, align: 'center' },
    {
      title: '错误原因',
      field: 'errors',
      minWidth: 300,
      className: 'import-error-col',
      slots: { default: 'errorsCell' },
    },
    {
      title: '经营平台订单号',
      field: 'platformOrderNo',
      minWidth: 150,
      showOverflow: 'tooltip',
    },
    {
      title: '店铺',
      field: 'shopName',
      minWidth: 120,
      showOverflow: 'tooltip',
    },
    { title: 'SKU', field: 'sku', minWidth: 130, showOverflow: 'tooltip' },
    {
      title: '商品名称',
      field: 'productName',
      minWidth: 160,
      showOverflow: 'tooltip',
    },
    { title: '数量', field: 'quantity', width: 70, align: 'center' },
    { title: '单价', field: 'unitPrice', width: 90, align: 'right' },
  ],
  data: [],
  keepSource: false,
};
const [ImportSuccessGrid, importSuccessGridApi] = useVbenVxeGrid({
  gridOptions: importSuccessGridOptions,
});
const [ImportFailGrid, importFailGridApi] = useVbenVxeGrid({
  gridOptions: importFailGridOptions,
});
watch(importSuccessRows, (rows) => {
  importSuccessGridApi.setGridOptions({
    data: rows,
    pagerConfig: { total: rows.length },
  });
});
watch(importFailRows, (rows) => {
  importFailGridApi.setGridOptions({
    data: rows,
    pagerConfig: { total: rows.length },
  });
});

// 购买面单对话框（T2）：尺寸 + 重量 + 服务下拉
const buyWaybillDialogOpen = ref(false);
const buyWaybillRow = ref<AuthorizedOrderListVo | null>(null);
const buyWaybillSubmitting = ref(false);
const buyWaybillServicesLoading = ref(false);
const buyWaybillServices = ref<ShippingServiceOption[]>([]);
const retryingWaybillId = ref<null | number>(null);
// 尺寸/重量、物流商/服务不预填默认值。
// 用户填齐 4 个字段后 watch 自动触发 handleQueryShippingServices，
// TK 返回候选后自动选 isDefault（或首项）+ 该商首个服务，费用文本同步展示。
const buyWaybillForm = reactive<{
  height: null | number;
  length: null | number;
  shippingProviderId: string;
  shippingServiceId: string;
  weight: null | number;
  width: null | number;
}>({
  length: null,
  width: null,
  height: null,
  weight: null,
  shippingProviderId: '',
  shippingServiceId: '',
});

const manualWaybillDialogOpen = ref(false);
const manualWaybillRow = ref<AuthorizedOrderListVo | null>(null);
const manualWaybillNo = ref('');
const manualWaybillFile = ref<File | null>(null);
const manualWaybillSubmitting = ref(false);

// 物流商去重：所有 service 按 shippingProviderId group 取唯一物流商
const buyWaybillProviders = computed(() => {
  const seen = new Map<string, { id: string; name: string }>();
  for (const s of buyWaybillServices.value) {
    if (s.shippingProviderId && !seen.has(s.shippingProviderId)) {
      seen.set(s.shippingProviderId, {
        id: s.shippingProviderId,
        name: s.shippingProviderName || s.shippingProviderId,
      });
    }
  }
  return [...seen.values()];
});

// 物流服务按选中的物流商过滤
const buyWaybillFilteredServices = computed(() => {
  if (!buyWaybillForm.shippingProviderId) return buyWaybillServices.value;
  return buyWaybillServices.value.filter(
    (s) => s.shippingProviderId === buyWaybillForm.shippingProviderId,
  );
});

function selectedShippingService() {
  const sid = buyWaybillForm.shippingServiceId;
  if (!sid) return null;
  return buyWaybillServices.value.find((s) => s.id === sid) ?? null;
}

// 当前选中服务对应的预估费用（纯文本展示）
const buyWaybillPriceDisplay = computed(() => {
  const svc = selectedShippingService();
  if (!svc) return '';
  const price = svc.price ?? '-';
  const currency = svc.currency ?? '';
  return `${price} ${currency}`.trim();
});

const purchaseForms = reactive<
  {
    id: number;
    purchaseAsin: string;
    purchaseLink: string;
    purchaseUserRemark: string;
  }[]
>([]);

const currentTab = computed(() =>
  STATUS_TABS.find((item) => item.value === activeTab.value),
);

const selectedText = computed(() => `已选择 ${selectedIds.value.length} 单`);

const visibleRows = computed(() => data.value);

function colorTagMeta(value?: string) {
  return (
    COLOR_TAG_OPTIONS.find((item) => item.value === (value || NO_COLOR_TAG)) ||
    COLOR_TAG_OPTIONS[0]
  );
}

function colorTagSaveValue(value: string): null | string {
  return value === NO_COLOR_TAG ? null : value;
}

async function handleColorTagChange(row: AuthorizedOrderListVo, event: Event) {
  const target = event.target as HTMLSelectElement;
  const colorTag = colorTagSaveValue(target.value);
  const previous = row.colorTag;
  row.colorTag = (colorTag || undefined) as AuthorizedOrderListVo['colorTag'];
  try {
    await saveAuthorizedOrderColorTag(row.id, colorTag);
    ElMessage.success('颜色标签已保存');
  } catch (error) {
    row.colorTag = previous;
    throw error;
  }
}

const isPurchaseEditMode = computed(() => purchaseDialogMode.value === 'edit');

// 编辑模式过滤赠品行（跟 purchaseForms 保持一致）；查看模式展示全部 items
const purchaseDialogItems = computed(() => {
  const items = purchaseDialogRow.value?.items ?? [];
  if (!isPurchaseEditMode.value) return items;
  return items.filter((it: AuthorizedOrderItemVo) => Number(it.isGift) !== 1);
});

async function load() {
  loading.value = true;
  try {
    const tab = currentTab.value;
    const commonFilter = {
      keyword: keyword.value || undefined,
      platformCode: tab?.platformCode || platformCode.value || undefined,
      shopKeyword: shopKeyword.value || undefined,
      colorTag: colorTag.value || undefined,
      waybillFileStatus: waybillFileStatus.value || undefined,
      hasPurchase: hasPurchase.value === '' ? undefined : hasPurchase.value,
      hasInbound: hasInbound.value === '' ? undefined : hasInbound.value,
      hasOutbound: hasOutbound.value === '' ? undefined : hasOutbound.value,
      hasPreOnline: hasPreOnline.value === '' ? undefined : hasPreOnline.value,
    };
    const [res, stats] = await Promise.all([
      listAuthorizedOrders({
        ...commonFilter,
        excludeManualPlatform: Boolean(tab?.value && !tab.platformCode),
        externalStatusMapped: tab?.externalStatusMapped,
        processStatus: tab?.processStatus,
        pageNum: pageNum.value,
        pageSize: pageSize.value,
      }),
      statsAuthorizedOrders(commonFilter).catch(() => ({})),
    ]);
    data.value = res?.rows ?? [];
    total.value = res?.total ?? 0;
    statsMap.value = stats ?? {};
    selectedIds.value = selectedIds.value.filter((id) =>
      data.value.some((item) => item.id === id),
    );
    // 异步预取本页涉及到的 TK 仓库列表，不阻塞列表展示
    void prefetchWarehouses();
  } catch (error: any) {
    ElMessage.error(error?.message || '加载授权订单列表失败');
  } finally {
    loading.value = false;
  }
}

async function prefetchWarehouses() {
  const shopIds = new Set<string>();
  for (const row of data.value) {
    if (row.tiktokShopId && !warehouseCache.value[row.tiktokShopId]) {
      shopIds.add(row.tiktokShopId);
    }
  }
  if (shopIds.size === 0) return;
  await Promise.all(
    [...shopIds].map(async (tiktokShopId) => {
      try {
        const list = await warehouseListApi({ tiktokShopId });
        const map: Record<string, WarehouseEntry> = {};
        for (const w of list ?? []) {
          if (w?.warehouseId) {
            map[w.warehouseId] = {
              name: w.warehouseName,
              addressLine1: w.addressLine1,
              addressLine2: w.addressLine2,
              city: w.city,
              contactPerson: w.contactPerson,
              fullAddress: w.fullAddress,
              phoneNumber: w.phoneNumber,
              state: w.state,
              postalCode: w.postalCode,
              isDefault: w.isDefault,
            };
          }
        }
        warehouseCache.value[tiktokShopId] = map;
      } catch {
        // 缓存空映射，避免重复重试
        warehouseCache.value[tiktokShopId] = {};
      }
    }),
  );
}

function warehouseOf(row: AuthorizedOrderListVo): null | WarehouseEntry {
  if (!row.tiktokShopId || !row.warehouseId) return null;
  const map = warehouseCache.value[row.tiktokShopId];
  return map?.[row.warehouseId] ?? null;
}

function handleSearch() {
  pageNum.value = 1;
  load();
}

function handleReset() {
  keyword.value = '';
  platformCode.value = '';
  shopKeyword.value = '';
  colorTag.value = '';
  batchColorTag.value = NO_COLOR_TAG;
  waybillFileStatus.value = '';
  hasPurchase.value = '';
  hasInbound.value = '';
  hasOutbound.value = '';
  hasPreOnline.value = '';
  pageNum.value = 1;
  load();
}

function clearSyncProgressTimer() {
  if (syncProgressTimer) {
    window.clearTimeout(syncProgressTimer);
    syncProgressTimer = undefined;
  }
}

function startSyncProgress() {
  clearSyncProgressTimer();
  syncing.value = true;
  syncProgress.value = 8;
  syncProgressText.value = '正在创建同步任务...';
}

function finishSyncProgress(text: string) {
  clearSyncProgressTimer();
  syncProgress.value = 100;
  syncProgressText.value = text;
}

function resetSyncProgress() {
  clearSyncProgressTimer();
  syncing.value = false;
  syncProgress.value = 0;
  syncProgressText.value = '';
}

function waitSyncProgressClose() {
  return new Promise((resolve) => window.setTimeout(resolve, 350));
}

// 后台异步同步：轮询 /sync-task/{taskId} 至终态；终态返回任务详情，超时返回 null
function pollSyncTask(taskId: number) {
  const terminal = new Set(['FAILED', 'PARTIAL', 'SUCCESS']);
  const intervalMs = 2000;
  const maxAttempts = 150; // 约 5 分钟兜底，超时则提示后台继续
  let attempts = 0;
  return new Promise<AuthorizedOrderSyncTaskVo | null>((resolve) => {
    const tick = async () => {
      attempts += 1;
      try {
        const detail = await getAuthorizedOrderSyncTask(taskId);
        if (detail && terminal.has(detail.taskStatus)) {
          resolve(detail);
          return;
        }
        if (syncProgress.value < 90) {
          syncProgress.value = Math.min(90, syncProgress.value + 6);
        }
        syncProgressText.value = '正在同步授权订单，请勿重复操作...';
      } catch {
        // 单次轮询失败（瞬时网络）忽略，继续重试
      }
      if (attempts >= maxAttempts) {
        resolve(null);
        return;
      }
      syncProgressTimer = window.setTimeout(tick, intervalMs);
    };
    syncProgressTimer = window.setTimeout(tick, intervalMs);
  });
}

async function handleSync() {
  if (selectedSyncShopIds.value.length === 0) {
    ElMessage.warning('请至少选择一个授权店铺');
    return;
  }
  if (syncing.value) return;
  syncDialogOpen.value = false;
  startSyncProgress();
  try {
    // 后端建任务后立即返回 PENDING 任务，真正同步在后台执行，这里轮询其终态
    const task = await syncAuthorizedOrders({
      platformCodes: ['TIKTOK'],
      shopIds: selectedSyncShopIds.value,
      syncType: 'INCREMENTAL',
    });
    syncProgress.value = 20;
    syncProgressText.value = '同步任务已创建，正在同步...';
    const result = await pollSyncTask(task.id);
    if (result) {
      const created = result.createdCount ?? 0;
      const updated = result.updatedCount ?? 0;
      const failed = result.failedCount ?? 0;
      if (result.taskStatus === 'FAILED') {
        finishSyncProgress('同步失败，正在关闭...');
        ElMessage.error(result.errorSummary || '同步授权订单失败');
      } else {
        finishSyncProgress('同步完成，正在刷新列表...');
        ElMessage.success(
          `同步完成：新增 ${created} 单，更新 ${updated} 单，失败 ${failed} 单`,
        );
      }
    } else {
      finishSyncProgress('同步仍在后台进行...');
      ElMessage.info('同步仍在后台进行，请稍后刷新列表查看结果');
    }
    await load();
  } catch (error: any) {
    finishSyncProgress('同步失败，正在关闭...');
    ElMessage.error(error?.message || '同步授权订单失败');
  } finally {
    await waitSyncProgressClose();
    resetSyncProgress();
  }
}

async function openSyncDialog() {
  if (syncing.value) return;
  syncShopLoading.value = true;
  try {
    const result = await getShopListApi({
      authStatus: 1,
      pageNum: 1,
      pageSize: 100,
    });
    syncShopOptions.value = (result.rows || []).map((shop: any) => ({
      authAppProfile: shop.authAppProfile,
      label: `${shop.shopAlias || shop.tiktokShopName}（${formatSyncShopType(shop.sellerType)}）`,
      region: shop.region,
      sellerType: shop.sellerType,
      value: shop.shopId,
    }));
    if (syncShopOptions.value.length === 0) {
      ElMessage.warning('暂无可同步的已授权店铺');
      return;
    }
    selectedSyncShopIds.value = [];
    syncDialogOpen.value = true;
  } catch (error: any) {
    ElMessage.error(error?.message || '加载授权店铺失败');
  } finally {
    syncShopLoading.value = false;
  }
}

function handleUpdate() {
  ElMessage.info('更新订单功能开发中');
}

async function handleBatchPurchase() {
  if (selectedIds.value.length === 0) {
    ElMessage.warning('请先勾选需要下单采购的授权订单');
    return;
  }
  try {
    const details = await Promise.all(
      selectedIds.value.map((id) => getAuthorizedOrderDetail(id)),
    );
    const onHoldOrders = details
      .filter((detail) => isOnHold(detail))
      .map((d) => d.platformOrderNo || d.authOrderNo || `#${d.id}`);
    if (onHoldOrders.length > 0) {
      ElMessage.warning(`${ON_HOLD_BLOCK_REASON}：${onHoldOrders.join(', ')}`);
      return;
    }
    const downstreamDisabledOrders = details
      .filter((d) => d.downstreamEnabled === 0)
      .map((d) => d.platformOrderNo || d.authOrderNo || `#${d.id}`);
    if (downstreamDisabledOrders.length > 0) {
      ElMessage.warning(
        `以下订单已暂停采购业务：${downstreamDisabledOrders.join(', ')}`,
      );
      return;
    }
    const incomplete = details
      .filter((d) => d.dataIntegrityStatus !== 'COMPLETE')
      .map((d) => d.platformOrderNo || d.authOrderNo || `#${d.id}`);
    if (incomplete.length > 0) {
      ElMessage.warning(
        `存在信息不完整的授权订单，无法下单：${incomplete.join(', ')}`,
      );
      return;
    }
    purchaseAddDrawerApi.setData({
      fromAuthorizedOrder: true,
      authorizedOrders: details,
      refresh: false,
    });
    purchaseAddDrawerApi.open();
  } catch (error: any) {
    ElMessage.error(error?.message || '打开批量采购失败');
  }
}

async function handleBatchColorTag() {
  if (selectedIds.value.length === 0) {
    ElMessage.warning('请先勾选授权订单');
    return;
  }
  try {
    await batchSaveAuthorizedOrderColorTag(
      selectedIds.value,
      colorTagSaveValue(batchColorTag.value),
    );
    ElMessage.success('批量颜色标签已保存');
    selectedIds.value = [];
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '批量保存颜色标签失败');
  }
}

function toggleRow(row: AuthorizedOrderListVo, checked: boolean) {
  if (checked) {
    selectedIds.value = [...new Set([row.id, ...selectedIds.value])];
    return;
  }
  selectedIds.value = selectedIds.value.filter((id) => id !== row.id);
}

function toggleAll(checked: boolean) {
  selectedIds.value = checked ? data.value.map((item) => item.id) : [];
}

async function openDrawer(row: AuthorizedOrderListVo) {
  currentRow.value = row;
  drawerOpen.value = true;
  try {
    currentRow.value = await getAuthorizedOrderDetail(row.id);
  } catch (error: any) {
    ElMessage.error(error?.message || '加载授权订单详情失败');
  }
}

function canDelete(row: AuthorizedOrderListVo) {
  return row.platformCode === 'MANUAL' && (row.purchaseOrderCount ?? 0) === 0;
}

async function handleDelete(row: AuthorizedOrderListVo) {
  try {
    await ElMessageBox.confirm(
      '确认删除该手工订单？删除后不可恢复。',
      '删除手工订单',
      { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' },
    );
  } catch {
    return;
  }
  try {
    await deleteAuthorizedOrder(row.id);
    ElMessage.success('手工订单已删除');
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '删除失败');
  }
}

function closeDrawer() {
  drawerOpen.value = false;
}

async function openPurchaseDialog(
  row: AuthorizedOrderListVo,
  mode: 'edit' | 'view',
) {
  if (mode === 'edit' && isOnHold(row)) {
    ElMessage.warning(ON_HOLD_BLOCK_REASON);
    return;
  }
  if (mode === 'edit' && row.downstreamEnabled === 0) {
    ElMessage.warning('当前订单已暂停采购业务');
    return;
  }
  purchaseDialogMode.value = mode;
  purchaseDialogOpen.value = true;
  purchaseDialogLoading.value = true;
  purchaseDialogRow.value = row;
  purchaseForms.splice(0);
  try {
    const detail = await getAuthorizedOrderDetail(row.id);
    purchaseDialogRow.value = detail;
    // 赠品（isGift=1）不参与采购信息编辑：TK 端赠品单价 0，不进采购流程，
    // 编辑对话框直接过滤掉，避免给赠品行配 ASIN / 备注却没用到
    purchaseForms.splice(
      0,
      purchaseForms.length,
      ...(detail.items ?? [])
        .filter((item) => Number(item.isGift) !== 1)
        .map((item) => ({
          id: item.id,
          purchaseAsin: item.purchaseAsin || '',
          purchaseLink: item.purchaseLink || '',
          purchaseUserRemark: item.purchaseUserRemark || '',
        })),
    );
  } catch (error: any) {
    ElMessage.error(error?.message || '加载采购信息失败');
  } finally {
    purchaseDialogLoading.value = false;
  }
}

function closePurchaseDialog() {
  purchaseDialogOpen.value = false;
}

function tabCount(tab: StatusTab) {
  // 「全部」走 stats.total；其它走 stats[tab.value]（key 跟后端 SUM(CASE WHEN ...) 别名对齐）
  if (!tab.value) return Number(statsMap.value.total ?? total.value ?? 0);
  return Number(statsMap.value[tab.value] ?? 0);
}

function isSelected(row: AuthorizedOrderListVo) {
  return selectedIds.value.includes(row.id);
}

function isAllSelected() {
  return (
    data.value.length > 0 && selectedIds.value.length === data.value.length
  );
}

function orderStatus(row: AuthorizedOrderListVo) {
  if (row.platformCode === 'MANUAL') return '手工订单';
  return (
    row.externalStatusMapped ||
    row.externalStatusRaw ||
    row.processStatusLabel ||
    processStatusLabel(row.processStatus)
  );
}

function orderSource(row?: { platformCode?: string; sellerType?: string }) {
  if (!row?.platformCode) return '-';
  if (row.platformCode === 'MANUAL') return '手工订单';
  if (!row.sellerType) return row.platformCode;
  return `${row.platformCode} ${formatSyncShopType(row.sellerType)}`;
}

function waybillStatus(row: AuthorizedOrderListVo) {
  if (canPreviewWaybill(row)) return '查看面单';
  if (row.tkPackageId || row.waybillFileStatus === 'GENERATED') return '已购买';
  const map: Record<string, string> = {
    NONE: '未购买',
    UPLOADED: '已上传',
    UPLOAD_FAILED: '面单获取失败',
  };
  return row.waybillFileStatus
    ? (map[row.waybillFileStatus] ?? row.waybillFileStatus)
    : '未购买';
}

function waybillTone(row: AuthorizedOrderListVo) {
  if (isAwaitingShipment(row)) return row.tkPackageId ? 'ok' : 'warn';
  const value = row.waybillFileStatus;
  if (value === 'GENERATED' || value === 'UPLOADED') return 'ok';
  if (value === 'UPLOAD_FAILED') return 'bad';
  return 'warn';
}

function firstProduct(row: AuthorizedOrderListVo) {
  return row.firstProductName || '授权订单商品';
}

function itemLines(row: AuthorizedOrderListVo) {
  if (row.items?.length) {
    return row.items.map((item, index) => ({
      currency: item.currency,
      imageUrl: item.imageUrl,
      index: item.lineNo || index + 1,
      isGift: item.isGift === 1,
      name: item.productName || firstProduct(row),
      quantity: item.quantity || 1,
      purchaseAsin: item.purchaseAsin,
      purchaseLink: item.purchaseLink,
      purchaseUserRemark: item.purchaseUserRemark,
      sku: item.sku || item.externalSkuId || '-',
      spec: item.spec || '默认规格',
      unitPrice: item.unitPrice,
    }));
  }
  return [
    {
      currency: row.firstCurrency,
      imageUrl: row.firstImageUrl,
      index: 1,
      isGift: false,
      name: firstProduct(row),
      quantity: row.firstQuantity || 1,
      purchaseAsin: row.firstSku || undefined,
      purchaseLink: undefined,
      purchaseUserRemark: undefined,
      sku: row.firstSku || '-',
      spec: '默认规格',
      unitPrice: row.firstUnitPrice,
    },
  ];
}

// fallback 占位 form：ElTable 重渲染 / 切赠品过滤瞬间，
// reactive 顺序可能让 purchaseForms 暂时找不到对应 form（item.id 不在）。
// 直接返回 undefined + v-model 上 `!` 强断言会抛错（console 9 个 TypeError）。
// 给个独立的 noop 对象当 fallback：用户输入不会写回 purchaseForms（编辑模式下
// purchaseForms 已过滤赠品对齐 purchaseDialogItems，正常路径不会走 fallback）。
function purchaseFormOf(item: AuthorizedOrderItemVo) {
  return (
    purchaseForms.find((form) => form.id === item.id) ?? {
      id: 0,
      purchaseAsin: '',
      purchaseLink: '',
      purchaseUserRemark: '',
    }
  );
}

function purchaseAsinText(item: ReturnType<typeof itemLines>[number]) {
  return item.purchaseAsin || '-';
}

function purchaseRemarkText(item: ReturnType<typeof itemLines>[number]) {
  return item.purchaseUserRemark || '-';
}

function commaText(values: Array<null | string | undefined>) {
  return (
    values
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join('，') || '-'
  );
}

function customerAddressText(row?: AuthorizedOrderListVo) {
  if (!row) return '-';
  return commaText([
    row.recipientName || row.buyerName,
    row.addressLine,
    row.city,
    row.stateProvince,
    row.postalCode,
    row.recipientPhone || row.buyerPhone,
  ]);
}

function warehouseAddressText(row?: AuthorizedOrderListVo) {
  if (!row) return '-';
  const warehouse = warehouseOf(row);
  if (!warehouse) return '-';
  return commaText([
    warehouse.name,
    warehouse.contactPerson,
    warehouse.addressLine1 || warehouse.fullAddress,
    warehouse.addressLine2,
    warehouse.city,
    warehouse.state,
    warehouse.postalCode,
    warehouse.phoneNumber,
  ]);
}

const router = useRouter();

type LinkType = 'INBOUND' | 'OUTBOUND' | 'PRE_ONLINE' | 'PURCHASE';

type LinkRowMeta = {
  count: number;
  disableReason: string;
  enabled: boolean;
  label: string;
  linkedId?: number;
  linkedNo?: string;
  links?: AuthorizedOrderLinkSummaryVo[];
  type: LinkType;
};

function relationLinks(
  refType: LinkType,
  links: AuthorizedOrderLinkSummaryVo[] | undefined,
  fallbackId?: number,
  fallbackNo?: string,
): AuthorizedOrderLinkSummaryVo[] {
  if (links && links.length > 0) return links;
  if (!fallbackId && !fallbackNo) return [];
  return [
    {
      refId: fallbackId || 0,
      refNo: fallbackNo,
      refType,
    },
  ];
}

function linkRowMeta(row: AuthorizedOrderListVo): LinkRowMeta[] {
  const downstreamDisabled = row.downstreamEnabled === 0;
  const fulfillmentBlocked = downstreamDisabled || isOnHold(row);
  const downstreamDisabledReason = isOnHold(row)
    ? ON_HOLD_BLOCK_REASON
    : '当前订单已暂停下游业务';
  return [
    {
      type: 'PURCHASE',
      label: '采购',
      count: row.purchaseOrderCount || 0,
      linkedId: row.firstPurchaseOrderId,
      linkedNo: row.firstPurchaseOrderNo,
      links: relationLinks(
        'PURCHASE',
        row.purchaseRels,
        row.firstPurchaseOrderId,
        row.firstPurchaseOrderNo,
      ),
      enabled: !fulfillmentBlocked && row.dataIntegrityStatus === 'COMPLETE',
      disableReason: fulfillmentBlocked
        ? downstreamDisabledReason
        : '订单信息不完整，请先补全后再关联采购',
    },
    {
      type: 'INBOUND',
      label: '入库',
      count: row.inboundOrderCount || 0,
      linkedId: row.firstInboundOrderId,
      linkedNo: row.firstInboundOrderNo,
      links: relationLinks(
        'INBOUND',
        row.inboundRels,
        row.firstInboundOrderId,
        row.firstInboundOrderNo,
      ),
      enabled: !fulfillmentBlocked,
      disableReason: fulfillmentBlocked ? downstreamDisabledReason : '',
    },
    {
      type: 'OUTBOUND',
      label: '出库',
      count: row.outboundOrderCount || 0,
      linkedId: row.firstOutboundOrderId,
      linkedNo: row.firstOutboundOrderNo,
      links: relationLinks(
        'OUTBOUND',
        row.outboundRels,
        row.firstOutboundOrderId,
        row.firstOutboundOrderNo,
      ),
      enabled: !fulfillmentBlocked,
      disableReason: fulfillmentBlocked ? downstreamDisabledReason : '',
    },
    {
      type: 'PRE_ONLINE',
      label: '预上网',
      count: row.preOnlineOrderCount || 0,
      linkedId: row.firstPreOnlineOrderId,
      linkedNo: row.firstPreOnlineOrderNo,
      links: relationLinks(
        'PRE_ONLINE',
        row.preOnlineRels,
        row.firstPreOnlineOrderId,
        row.firstPreOnlineOrderNo,
      ),
      // 预上网不依赖前置关联，任意时刻可操作
      enabled: !fulfillmentBlocked,
      disableReason: fulfillmentBlocked ? downstreamDisabledReason : '',
    },
  ];
}

const LINK_TYPE_PATH: Record<LinkType, string> = {
  PURCHASE: '/purchase-order/index',
  INBOUND: '/inbound-order/index',
  OUTBOUND: '/inbound-order/outbound',
  PRE_ONLINE: '/order-management/pre-online',
};

function jumpToLinked(link: LinkRowMeta) {
  if (!link.linkedNo) return;
  // 入库订单：在当前页直接弹查看对话框，不跳页
  if (link.type === 'INBOUND') {
    if (!link.linkedId) {
      ElMessage.warning('未找到入库订单 ID');
      return;
    }
    inboundDetailRow.value = { id: link.linkedId };
    inboundDetailVisible.value = true;
    return;
  }
  // 预上网：同样弹查看对话框
  if (link.type === 'PRE_ONLINE') {
    if (!link.linkedId) {
      ElMessage.warning('未找到预上网订单 ID');
      return;
    }
    preOnlineDetailRow.value = { id: link.linkedId };
    preOnlineDetailVisible.value = true;
    return;
  }
  const path = LINK_TYPE_PATH[link.type];
  router.push({ path, query: { keyword: link.linkedNo } });
}

// 复用采购订单页的批量上传 drawer，授权订单提交时透传 authorizedOrderId
// → 后端 PurchaseOrderImportMethod 自动写 fq_authorized_order_link_rel(PURCHASE, AUTO)
const [PurchaseAddDrawer, purchaseAddDrawerApi] = useVbenDrawer({
  connectedComponent: PurchaseAdd,
  onClosed() {
    const data = purchaseAddDrawerApi.getData<Record<string, any>>() || {};
    if (data.refresh) {
      load();
    }
  },
});

// 入库关联：复用 InboundOrderCreateDialog，提交成功后 @created 拿
// inboundOrderId 调 linkAuthorizedOrderInbound 写 link_rel
const inboundDialogOpen = ref(false);
const inboundPresetAuth = ref<AuthorizedOrderDetailVo>();
const inboundLinkAuthId = ref<number>();
const inboundPresetPurchaseIds = ref<number[]>([]);
// 点入库订单号时弹查看对话框（不跳页）
const inboundDetailVisible = ref(false);
const inboundDetailRow = ref<null | { id: number }>(null);
const outboundDialogOpen = ref(false);
const outboundPresetAuth = ref<AuthorizedOrderDetailVo>();
const outboundLinkAuthId = ref<number>();
// 点预上网订单号时弹查看对话框
const preOnlineDetailVisible = ref(false);
const preOnlineDetailRow = ref<null | { id: number }>(null);

// 预上网关联：单独的 dialog，预填店铺名 + 平台订单号
const preOnlineDialogOpen = ref(false);
const preOnlinePresetAuth = ref<AuthorizedOrderDetailVo>();
const preOnlineLinkAuthId = ref<number>();

async function onPreOnlineCreated(preOnlineOrderId: number) {
  if (!preOnlineLinkAuthId.value || !preOnlineOrderId) return;
  try {
    await linkAuthorizedOrderPreOnline(preOnlineLinkAuthId.value, [
      preOnlineOrderId,
    ]);
    ElMessage.success('预上网订单已创建并关联');
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '关联预上网订单失败');
  } finally {
    preOnlineLinkAuthId.value = undefined;
    preOnlinePresetAuth.value = undefined;
  }
}

async function onInboundCreated(inboundOrderId: number) {
  if (!inboundLinkAuthId.value || !inboundOrderId) return;
  try {
    await linkAuthorizedOrderInbound(inboundLinkAuthId.value, [inboundOrderId]);
    ElMessage.success('入库订单已创建并关联');
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '关联入库订单失败');
  } finally {
    inboundLinkAuthId.value = undefined;
    inboundPresetAuth.value = undefined;
  }
}

async function onOutboundCreated(outboundOrderId?: number) {
  if (!outboundLinkAuthId.value || !outboundOrderId) return;
  try {
    await linkAuthorizedOrderOutbound(outboundLinkAuthId.value, [
      outboundOrderId,
    ]);
    ElMessage.success('出库订单已创建并关联');
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '关联出库订单失败');
  } finally {
    outboundLinkAuthId.value = undefined;
    outboundPresetAuth.value = undefined;
  }
}

async function openLinkDialog(row: AuthorizedOrderListVo, type: LinkType) {
  if (type === 'PURCHASE') {
    try {
      // 拉最新 detail 拿 items 给 drawer 预填
      const detail = await getAuthorizedOrderDetail(row.id);
      purchaseAddDrawerApi.setData({
        fromAuthorizedOrder: true,
        authorizedOrder: detail,
        refresh: false,
      });
      purchaseAddDrawerApi.open();
    } catch (error: any) {
      ElMessage.error(error?.message || '打开采购关联失败');
    }
    return;
  }
  if (type === 'INBOUND') {
    try {
      const detail = await getAuthorizedOrderDetail(row.id);
      inboundPresetAuth.value = detail;
      inboundLinkAuthId.value = row.id;
      // 已关联的采购订单 id 列表（linkRels ref_type=PURCHASE，purchaseRels 同等）
      const purchaseLinks = (detail.linkRels || []).filter(
        (rel: any) => rel.refType === 'PURCHASE',
      );
      inboundPresetPurchaseIds.value = purchaseLinks
        .map((rel: any) => Number(rel.refId))
        .filter((id: number) => !Number.isNaN(id));
      inboundDialogOpen.value = true;
    } catch (error: any) {
      ElMessage.error(error?.message || '打开入库关联失败');
    }
    return;
  }
  if (type === 'PRE_ONLINE') {
    try {
      const detail = await getAuthorizedOrderDetail(row.id);
      preOnlinePresetAuth.value = detail;
      preOnlineLinkAuthId.value = row.id;
      preOnlineDialogOpen.value = true;
    } catch (error: any) {
      ElMessage.error(error?.message || '打开预上网关联失败');
    }
    return;
  }
  if (type === 'OUTBOUND') {
    try {
      const detail = await getAuthorizedOrderDetail(row.id);
      outboundPresetAuth.value = detail;
      outboundLinkAuthId.value = row.id;
      outboundDialogOpen.value = true;
    } catch (error: any) {
      ElMessage.error(error?.message || '打开出库关联失败');
    }
    return;
  }
  const labelMap: Record<LinkType, string> = {
    PURCHASE: '采购订单',
    INBOUND: '入库订单',
    OUTBOUND: '出库订单',
    PRE_ONLINE: '预上网订单',
  };
  ElMessage.info(
    `「关联${labelMap[type]}」对话框开发中（授权订单 #${row.id}）`,
  );
}

function previewWaybill(row: AuthorizedOrderListVo) {
  if (!canPreviewWaybill(row)) {
    ElMessage.warning('当前订单暂无面单文件');
    return;
  }
  waybillDialogUrl.value = row.waybillFileUrl || '';
  waybillDialogNo.value = row.waybillNo || '';
  waybillDialogOpen.value = true;
}

// 购买面单按钮可见性：未买（status 空 / NONE）+ 非取消单 + 已有 TK 发货仓 ID。
// UPLOAD_FAILED 可能已经在 TK 创建过 package，不能让用户重复扣费购买。
function canBuyWaybill(row: AuthorizedOrderListVo) {
  if (isOnHold(row)) return false;
  if (row.downstreamEnabled === 0) return false;
  if (!row?.warehouseId) return false;
  // MANUAL 平台导入的订单没有 TK 店铺关联，物理上调不动 TK fulfillment 接口
  if (row.platformCode === 'MANUAL') return false;
  if (row.processStatus === 'CANCELED') return false;
  if (row.tkPackageId) return false;
  if (!isWaybillBuyStatus(row)) return false;
  const s = row.waybillFileStatus || 'NONE';
  return s === 'NONE';
}

function canPreviewWaybill(row: AuthorizedOrderListVo) {
  return Boolean(row.waybillFileUrl);
}

function canUploadManualWaybill(row: AuthorizedOrderListVo) {
  if (isOnHold(row)) return false;
  if (row.downstreamEnabled === 0) return false;
  if (row.platformCode !== 'MANUAL') return false;
  if (row.processStatus === 'CANCELED') return false;
  return !row.waybillFileUrl;
}

function canRetryWaybill(row: AuthorizedOrderListVo) {
  if (isOnHold(row)) return false;
  if (row.downstreamEnabled === 0) return false;
  if (row.platformCode === 'MANUAL') return false;
  if (row.processStatus === 'CANCELED') return false;
  if (row.waybillFileUrl) return false;
  if (!row.tkPackageId) return false;
  if (!isAwaitingCollection(row)) return false;
  const s = row.waybillFileStatus || 'NONE';
  return s === 'GENERATED' || s === 'UPLOAD_FAILED';
}

function waybillUnavailableReason(row: AuthorizedOrderListVo) {
  if (isOnHold(row)) return ON_HOLD_BLOCK_REASON;
  if (row.downstreamEnabled === 0) return '当前订单已暂停面单业务';
  if (
    canPreviewWaybill(row) ||
    canBuyWaybill(row) ||
    canRetryWaybill(row) ||
    canUploadManualWaybill(row)
  )
    return '';
  const s = row.waybillFileStatus || 'NONE';
  if (isWaybillBuyStatus(row)) {
    return row.tkPackageId ? '面单已购买，等待订单状态同步至待揽收' : '';
  }
  if (s === 'UPLOAD_FAILED') return '面单获取失败，请联系客服';
  if (s === 'GENERATED') return '待揽收后可获取面单';
  if (s === 'UPLOADED') return '面单文件缺失，请联系客服';
  if (row.processStatus === 'CANCELED') return '订单已取消，无法购买';
  if (row.platformCode === 'MANUAL') return '请上传官方面单';
  if (!row.warehouseId) return '缺少发货仓，无法购买';
  return '';
}

function waybillHint(row: AuthorizedOrderListVo) {
  if (isOnHold(row)) return '等待TikTok开放履约后自动刷新地址';
  if (row.waybillNo) return `物流单号：${row.waybillNo}`;
  if (canRetryWaybill(row)) return 'TK 包裹已创建，可重新获取面单文件';
  if (canUploadManualWaybill(row)) return '上传官方 PDF 面单';
  return canBuyWaybill(row) ? '点击「购买面单」走 TK 流程购买面单' : '-';
}

function isAwaitingShipment(row: AuthorizedOrderListVo) {
  return row.externalStatusRaw === 'AWAITING_SHIPMENT';
}

function isPartiallyShipping(row: AuthorizedOrderListVo) {
  return row.externalStatusRaw === 'PARTIALLY_SHIPPING';
}

function isAwaitingCollection(row: AuthorizedOrderListVo) {
  return row.externalStatusRaw === 'AWAITING_COLLECTION';
}

function isWaybillBuyStatus(row: AuthorizedOrderListVo) {
  return isAwaitingShipment(row) || isPartiallyShipping(row);
}

function displayShopName(row?: { shopAlias?: string; shopName?: string }) {
  if (!row) return '-';
  return row.shopAlias || row.shopName || '-';
}

function openBuyWaybillDialog(row: AuthorizedOrderListVo) {
  buyWaybillRow.value = row;
  buyWaybillForm.length = null;
  buyWaybillForm.width = null;
  buyWaybillForm.height = null;
  buyWaybillForm.weight = null;
  buyWaybillForm.shippingProviderId = '';
  buyWaybillForm.shippingServiceId = '';
  buyWaybillServices.value = [];
  buyWaybillDialogOpen.value = true;
  // 不预设默认值；用户填齐 4 个字段后 watch 自动触发查询
}

function openManualWaybillDialog(row: AuthorizedOrderListVo) {
  manualWaybillRow.value = row;
  manualWaybillNo.value = row.waybillNo || '';
  manualWaybillFile.value = null;
  manualWaybillDialogOpen.value = true;
}

function buyWaybillParam(): AuthorizedOrderWaybillParam | null {
  const f = buyWaybillForm;
  if (
    f.length === null ||
    f.width === null ||
    f.height === null ||
    f.weight === null
  ) {
    return null;
  }
  return {
    length: String(f.length),
    width: String(f.width),
    height: String(f.height),
    weight: String(f.weight),
    shippingServiceId: f.shippingServiceId || undefined,
  };
}

async function handleQueryShippingServices() {
  if (!buyWaybillRow.value) return;
  const param = buyWaybillParam();
  if (!param) return; // 尺寸/重量没填全，静默跳过
  buyWaybillServicesLoading.value = true;
  try {
    const list = await listWaybillShippingServices(
      buyWaybillRow.value.id,
      param,
    );
    buyWaybillServices.value = list ?? [];
    if (buyWaybillServices.value.length === 0) {
      buyWaybillForm.shippingProviderId = '';
      buyWaybillForm.shippingServiceId = '';
      return;
    }
    // 默认选 isDefault 或第一项的物流商 + 该商下首个服务
    const def =
      buyWaybillServices.value.find((s) => s.isDefault) ??
      buyWaybillServices.value[0]!;
    buyWaybillForm.shippingProviderId = def.shippingProviderId || '';
    buyWaybillForm.shippingServiceId = def.id;
  } catch (error: any) {
    ElMessage.error(error?.message || '查询物流服务失败');
  } finally {
    buyWaybillServicesLoading.value = false;
  }
}

// 切换物流商：重置服务到该商下首项
function onShippingProviderChange(providerId: string) {
  buyWaybillForm.shippingProviderId = providerId;
  const first = buyWaybillServices.value.find(
    (s) => s.shippingProviderId === providerId,
  );
  buyWaybillForm.shippingServiceId = first?.id ?? '';
}

// 尺寸/重量变化不再自动触发查询，改成显式点「查询可用物流服务」按钮，
// 跟平台端体验一致 — 失焦自动查会在用户连续改尺寸时反复打 TK 接口，体验差。

async function onImportFileChange(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0] ?? null;
  importFile.value = file;
  // 清掉之前的预览数据
  importTotalRows.value = 0;
  importSuccessRows.value = [];
  importFailRows.value = [];
  importFinalResult.value = null;
  if (!file) return;
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    ElMessage.warning('仅支持 .xlsx 文件');
    return;
  }
  // 选完文件立刻调 check，前端就能在 drawer 里预览成功/失败
  importCheckLoading.value = true;
  try {
    const res = await importAuthorizedOrderCheck(file);
    importTotalRows.value = res?.totalRows ?? 0;
    importSuccessRows.value = res?.successList ?? [];
    importFailRows.value = res?.failList ?? [];
    importActiveTab.value =
      importSuccessRows.value.length > 0 ? 'success' : 'fail';
  } catch (error: any) {
    ElMessage.error(error?.message || '解析校验失败');
  } finally {
    importCheckLoading.value = false;
  }
}

const importTemplateLoading = ref(false);
async function handleDownloadImportTemplate() {
  importTemplateLoading.value = true;
  try {
    await downloadAuthorizedOrderTemplate();
  } catch (error: any) {
    ElMessage.error(error?.message || '模板下载失败');
  } finally {
    importTemplateLoading.value = false;
  }
}

function resetImportState() {
  importFile.value = null;
  importTotalRows.value = 0;
  importSuccessRows.value = [];
  importFailRows.value = [];
  importFinalResult.value = null;
  if (importFileInputRef.value) importFileInputRef.value.value = '';
}

async function submitImport() {
  if (importSuccessRows.value.length === 0) {
    ElMessage.warning('没有可导入的数据；请先选择文件解析或修正失败行');
    return;
  }
  importSubmitting.value = true;
  try {
    const res = await importAuthorizedOrderDo(importSuccessRows.value);
    importFinalResult.value = res ?? null;
    const created = res?.createdCount ?? 0;
    const skipped = res?.skippedExistsCount ?? 0;
    ElMessage.success(`导入完成：新增 ${created} 单，跳过已存在 ${skipped} 单`);
    if (created > 0) await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '导入失败');
  } finally {
    importSubmitting.value = false;
  }
}

async function handleBuyWaybill() {
  if (!buyWaybillRow.value) return;
  const param = buyWaybillParam();
  if (!param) {
    ElMessage.warning('请完整填写长宽高 + 重量');
    return;
  }
  if (!param.shippingServiceId) {
    ElMessage.warning('请先查询并选择物流服务');
    return;
  }
  buyWaybillSubmitting.value = true;
  try {
    await buyAuthorizedOrderWaybill(buyWaybillRow.value.id, param);
    ElMessage.success('面单购买成功');
    buyWaybillDialogOpen.value = false;
    await load();
  } catch (error: any) {
    const message = error?.message || '购买面单失败';
    ElMessage.error(message);
    if (message.includes('面单已购买') || message.includes('TK 包裹已创建')) {
      buyWaybillDialogOpen.value = false;
      await load();
    }
  } finally {
    buyWaybillSubmitting.value = false;
  }
}

async function handleRetryWaybill(row: AuthorizedOrderListVo) {
  retryingWaybillId.value = row.id;
  try {
    await retryAuthorizedOrderWaybill(row.id);
    ElMessage.success('面单获取成功');
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '面单暂未生成，请稍后重新获取');
    await load();
  } finally {
    retryingWaybillId.value = null;
  }
}

function handleManualWaybillFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  manualWaybillFile.value = input.files?.[0] ?? null;
}

async function handleManualWaybillUpload() {
  if (!manualWaybillRow.value) return;
  const waybillNo = manualWaybillNo.value.trim();
  if (!waybillNo) {
    ElMessage.warning('请填写物流单号');
    return;
  }
  if (!manualWaybillFile.value) {
    ElMessage.warning('请上传 PDF 面单文件');
    return;
  }
  if (!/\.pdf$/i.test(manualWaybillFile.value.name)) {
    ElMessage.warning('面单文件仅支持 PDF 格式');
    return;
  }
  manualWaybillSubmitting.value = true;
  try {
    await uploadManualAuthorizedOrderWaybill(
      manualWaybillRow.value.id,
      waybillNo,
      manualWaybillFile.value,
    );
    ElMessage.success('官方面单已上传');
    manualWaybillDialogOpen.value = false;
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '上传官方面单失败');
  } finally {
    manualWaybillSubmitting.value = false;
  }
}

function closeWaybillDialog() {
  waybillDialogOpen.value = false;
  waybillDialogUrl.value = '';
  waybillDialogNo.value = '';
}

function isWaybillImage(url: string) {
  return /\.(?:jpe?g|png|gif|webp|bmp|svg)(?:\?|#|$)/i.test(url);
}

function isWaybillPdf(url: string) {
  return /\.pdf(?:\?|#|$)/i.test(url);
}

function openWaybillInNewTab() {
  if (waybillDialogUrl.value) {
    window.open(waybillDialogUrl.value, '_blank', 'noopener,noreferrer');
  }
}

async function savePurchaseInfo() {
  if (!purchaseDialogRow.value) return;
  for (const form of purchaseForms) {
    if (!form.purchaseAsin.trim()) {
      ElMessage.error('采购ASIN不能为空');
      return;
    }
  }
  purchaseDialogSubmitting.value = true;
  try {
    await saveAuthorizedOrderItemPurchaseInfo(
      purchaseDialogRow.value.id,
      purchaseForms.map((form) => ({
        id: form.id,
        purchaseAsin: form.purchaseAsin.trim(),
        purchaseLink: form.purchaseLink.trim(),
        purchaseUserRemark: form.purchaseUserRemark.trim(),
      })),
    );
    ElMessage.success('采购信息已保存');
    closePurchaseDialog();
    await load();
  } catch (error: any) {
    ElMessage.error(error?.message || '保存采购信息失败');
  } finally {
    purchaseDialogSubmitting.value = false;
  }
}

function priceText(item: any) {
  if (item.unitPrice === undefined || item.unitPrice === null) return '-';
  const value = Number(item.unitPrice);
  const amount = Number.isNaN(value) ? item.unitPrice : value.toFixed(2);
  return item.currency ? `${item.currency} ${amount}` : `${amount}`;
}

watch(activeTab, () => {
  pageNum.value = 1;
  load();
});

onMounted(load);

onUnmounted(() => {
  clearSyncProgressTimer();
});
</script>

<template>
  <div class="auth-order-workbench">
    <ElDialog
      v-model="syncDialogOpen"
      title="选择要同步的授权店铺"
      width="520px"
    >
      <ElForm label-position="top">
        <ElFormItem label="授权店铺">
          <ElSelect
            v-model="selectedSyncShopIds"
            class="w-full"
            collapse-tags
            collapse-tags-tooltip
            filterable
            multiple
            placeholder="请选择一个或多个店铺"
          >
            <ElOption
              v-for="shop in syncShopOptions"
              :key="shop.value"
              :label="shop.label"
              :value="shop.value"
            />
          </ElSelect>
        </ElFormItem>
      </ElForm>
      <div class="text-sm text-gray-500">
        首次同步最近 30 天订单；后续按更新时间增量同步并自动回看 5 分钟。
      </div>
      <template #footer>
        <ElButton @click="syncDialogOpen = false">取消</ElButton>
        <ElButton
          type="primary"
          :disabled="selectedSyncShopIds.length === 0"
          @click="handleSync"
        >
          开始同步
        </ElButton>
      </template>
    </ElDialog>
    <div v-if="syncing" class="sync-progress-mask">
      <div class="sync-progress-panel">
        <div class="sync-progress-title">同步授权订单</div>
        <div class="sync-progress-text">{{ syncProgressText }}</div>
        <div class="sync-progress-track">
          <div
            class="sync-progress-bar"
            :style="{ width: `${syncProgress}%` }"
          ></div>
        </div>
        <div class="sync-progress-percent">{{ syncProgress }}%</div>
      </div>
    </div>
    <header class="page-header">
      <div class="title-block">
        <h1>授权订单</h1>
        <span class="breadcrumb">工作台 / 订单管理 / 授权订单</span>
      </div>
      <div class="header-actions">
        <span>消息</span>
        <span>客服</span>
        <strong>测试账号</strong>
      </div>
    </header>

    <section class="content">
      <ElTabs v-model="activeTab" class="auth-status-tabs mb-3">
        <ElTabPane
          v-for="tab in STATUS_TABS"
          :key="tab.value"
          :label="`${tab.label}(${tabCount(tab)})`"
          :name="tab.value"
        />
      </ElTabs>

      <section class="query-bar">
        <label class="field field-search">
          搜索
          <input
            v-model="keyword"
            placeholder="TikTok 订单号 / 店铺 / 商品名称 / SKU / ASIN"
            @keyup.enter="handleSearch"
          />
        </label>
        <label class="field">
          店铺
          <input
            v-model="shopKeyword"
            placeholder="店铺别称/店铺全名"
            @keyup.enter="handleSearch"
          />
        </label>
        <label class="field">
          标签
          <select v-model="colorTag">
            <option
              v-for="item in COLOR_TAG_FILTER_OPTIONS"
              :key="item.value || 'all'"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
        <label class="field">
          平台
          <select v-model="platformCode">
            <option value="">全部平台</option>
            <option
              v-for="item in PLATFORM_OPTIONS"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
        <label class="field">
          面单
          <select v-model="waybillFileStatus">
            <option
              v-for="item in WAYBILL_OPTIONS"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </label>
        <label class="field">
          关联采购
          <select v-model="hasPurchase">
            <option value="">全部</option>
            <option :value="true">是</option>
            <option :value="false">否</option>
          </select>
        </label>
        <label class="field">
          关联入库
          <select v-model="hasInbound">
            <option value="">全部</option>
            <option :value="true">是</option>
            <option :value="false">否</option>
          </select>
        </label>
        <label class="field">
          关联出库
          <select v-model="hasOutbound">
            <option value="">全部</option>
            <option :value="true">是</option>
            <option :value="false">否</option>
          </select>
        </label>
        <label class="field">
          关联预上网
          <select v-model="hasPreOnline">
            <option value="">全部</option>
            <option :value="true">是</option>
            <option :value="false">否</option>
          </select>
        </label>
        <div class="query-actions">
          <button class="btn" type="button" @click="handleReset">重置</button>
          <button class="btn primary" type="button" @click="handleSearch">
            查询
          </button>
        </div>
      </section>

      <section class="table-card">
        <div class="table-toolbar">
          <div class="toolbar-left">
            <button
              class="btn primary"
              type="button"
              @click="handleBatchPurchase"
            >
              批量下单采购
            </button>
            <select
              v-model="batchColorTag"
              class="toolbar-select"
              :disabled="selectedIds.length === 0"
            >
              <option
                v-for="item in COLOR_TAG_OPTIONS"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </option>
            </select>
            <button
              class="btn"
              :disabled="selectedIds.length === 0"
              type="button"
              @click="handleBatchColorTag"
            >
              批量标记
            </button>
            <button
              class="btn"
              :disabled="syncing"
              type="button"
              @click="openSyncDialog"
            >
              {{ syncing || syncShopLoading ? '同步中' : '同步订单' }}
            </button>
            <button class="btn" type="button" @click="handleUpdate">
              更新订单
            </button>
            <button class="btn" type="button" @click="importDialogOpen = true">
              导入授权订单
            </button>
            <span class="selection-text">{{ selectedText }}</span>
          </div>
          <button class="btn" type="button" @click="load">刷新</button>
        </div>

        <div class="table-wrap">
          <table v-loading="loading">
            <thead>
              <tr>
                <th class="check-col">
                  <input
                    :checked="isAllSelected()"
                    type="checkbox"
                    @change="
                      toggleAll(($event.target as HTMLInputElement).checked)
                    "
                  />
                </th>
                <th class="color-tag-col">标签</th>
                <th>授权订单信息</th>
                <th>商品信息</th>
                <th>采购信息</th>
                <th>关联订单</th>
                <th>官方面单</th>
                <th>金额信息</th>
                <th>地址信息</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="visibleRows.length === 0">
                <td class="empty-cell" colspan="10">暂无授权订单</td>
              </tr>
              <tr v-for="row in visibleRows" :key="row.id">
                <td class="check-col">
                  <input
                    :checked="isSelected(row)"
                    type="checkbox"
                    @change="
                      toggleRow(
                        row,
                        ($event.target as HTMLInputElement).checked,
                      )
                    "
                  />
                </td>
                <td class="color-tag-col">
                  <div class="color-tag-cell">
                    <span
                      class="color-tag-dot"
                      :class="{ empty: !row.colorTag }"
                      :style="{
                        backgroundColor: colorTagMeta(row.colorTag).color,
                      }"
                    ></span>
                    <select
                      class="color-tag-select"
                      :value="row.colorTag || NO_COLOR_TAG"
                      @change="handleColorTagChange(row, $event)"
                    >
                      <option
                        v-for="item in COLOR_TAG_OPTIONS"
                        :key="item.value || 'none'"
                        :value="item.value"
                      >
                        {{ item.label }}
                      </option>
                    </select>
                  </div>
                </td>
                <td class="order-col">
                  <div class="order-no">
                    {{ dash(row.platformOrderNo || row.authOrderNo) }}
                  </div>
                  <div class="subline">来源：{{ orderSource(row) }}</div>
                  <div class="shop-name">
                    店铺名称：{{ displayShopName(row) }}
                  </div>
                  <div class="subline">
                    付款：{{ formatDateTime(row.externalPaidAt) }}
                  </div>
                  <div class="tag-list">
                    <span class="status info">{{ orderStatus(row) }}</span>
                    <span class="pill">共 {{ row.itemCount || 0 }} 件</span>
                  </div>
                </td>
                <td class="product-col">
                  <div class="product-list">
                    <div
                      v-for="item in itemLines(row)"
                      :key="`${row.id}-${item.index}`"
                      class="product-mini"
                    >
                      <div
                        class="mini-thumb product-image-preview"
                        :class="{ 'has-image': item.imageUrl }"
                        :tabindex="item.imageUrl ? 0 : undefined"
                        :title="item.imageUrl ? '悬停查看大图' : ''"
                      >
                        <img
                          v-if="item.imageUrl"
                          :alt="item.name"
                          class="thumb-image"
                          :src="item.imageUrl"
                        />
                        <img
                          v-if="item.imageUrl"
                          alt=""
                          aria-hidden="true"
                          class="image-hover-preview"
                          :src="item.imageUrl"
                        />
                      </div>
                      <div class="product-mini-body">
                        <div class="mini-title">
                          <span v-if="item.isGift" class="gift-tag">赠品</span>
                          {{ item.name }}
                        </div>
                        <div class="product-meta">
                          <span>规格：{{ item.spec }}</span>
                          <span>数量：x{{ item.quantity }}</span>
                          <span>SKU：{{ item.sku }}</span>
                          <span>单价：{{ priceText(item) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="progress-cell purchase-info-cell">
                  <div class="cell-action-bar">
                    <ElTooltip
                      :disabled="!isOnHold(row)"
                      :content="ON_HOLD_BLOCK_REASON"
                      placement="top"
                    >
                      <span>
                        <ElButton
                          size="small"
                          link
                          type="primary"
                          :disabled="isOnHold(row)"
                          @click="openPurchaseDialog(row, 'edit')"
                        >
                          编辑
                        </ElButton>
                      </span>
                    </ElTooltip>
                    <ElButton
                      size="small"
                      link
                      @click="openPurchaseDialog(row, 'view')"
                    >
                      查看
                    </ElButton>
                  </div>
                  <div class="purchase-mini-list">
                    <div
                      v-for="item in itemLines(row)"
                      :key="`${row.id}-purchase-${item.index}`"
                      class="purchase-mini"
                    >
                      <span class="mini-index">商品{{ item.index }}</span>
                      <div class="purchase-display">
                        <span>数量：x{{ item.quantity }}</span>
                        <span>采购ASIN：{{ purchaseAsinText(item) }}</span>
                        <span>采购链接：{{ item.purchaseLink || '-' }}</span>
                        <span>用户备注：{{ purchaseRemarkText(item) }}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="assoc">
                    <div
                      v-for="link in linkRowMeta(row)"
                      :key="link.type"
                      class="assoc-row"
                    >
                      <span class="assoc-label">{{ link.label }}</span>
                      <template v-if="link.links && link.links.length > 0">
                        <button
                          v-for="rel in link.links"
                          :key="`${link.type}-${rel.refId}`"
                          class="link blue"
                          type="button"
                          @click="
                            jumpToLinked({
                              ...link,
                              linkedId: rel.refId,
                              linkedNo: rel.refNo || String(rel.refId),
                            })
                          "
                        >
                          {{ rel.refNo || rel.refId }}
                        </button>
                      </template>
                      <template v-else>
                        <ElTooltip
                          :disabled="link.enabled"
                          :content="link.disableReason"
                          placement="top"
                        >
                          <ElButton
                            link
                            size="small"
                            type="primary"
                            :disabled="!link.enabled"
                            @click="openLinkDialog(row, link.type)"
                          >
                            去关联
                          </ElButton>
                        </ElTooltip>
                      </template>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="label-cell">
                    <div class="label-line">
                      <span class="status" :class="waybillTone(row)">
                        {{ waybillStatus(row) }}
                      </span>
                      <button
                        v-if="canPreviewWaybill(row)"
                        class="link blue"
                        type="button"
                        @click="previewWaybill(row)"
                      >
                        查看面单
                      </button>
                      <button
                        v-if="canBuyWaybill(row)"
                        class="link blue"
                        type="button"
                        @click="openBuyWaybillDialog(row)"
                      >
                        购买面单
                      </button>
                      <button
                        v-if="canUploadManualWaybill(row)"
                        class="link blue"
                        type="button"
                        @click="openManualWaybillDialog(row)"
                      >
                        上传面单
                      </button>
                      <ElButton
                        v-if="canRetryWaybill(row)"
                        class="inline-action"
                        link
                        type="primary"
                        :loading="retryingWaybillId === row.id"
                        @click="handleRetryWaybill(row)"
                      >
                        重新获取面单
                      </ElButton>
                      <span
                        v-else-if="waybillUnavailableReason(row)"
                        class="muted"
                      >
                        {{ waybillUnavailableReason(row) }}
                      </span>
                    </div>
                    <div class="subline">
                      {{ waybillHint(row) }}
                    </div>
                  </div>
                </td>
                <td class="finance-col">
                  <div v-if="isApiAuthorizedOrder(row)" class="finance-summary">
                    <div>
                      <span>顾客实际付款</span>
                      <strong>{{
                        financeMoney(row, row.customerPaidAmount)
                      }}</strong>
                    </div>
                    <div>
                      <span>TikTok优惠补贴</span>
                      <strong
                        v-text="financeMoney(row, row.tiktokSubsidyAmount)"
                      ></strong>
                    </div>
                    <div class="finance-settlement-line">
                      <span>
                        预计结算金额
                        <small
                          v-if="
                            row.settlementAmountSource === 'SYSTEM_ESTIMATE'
                          "
                          class="finance-estimate-badge"
                        >
                          <span>系统估算</span>
                        </small>
                      </span>
                      <strong>{{
                        financeMoney(row, row.settlementDisplayAmount)
                      }}</strong>
                    </div>
                    <div>
                      <span>采购成本</span>
                      <strong>{{ financeMoney(row, row.purchaseCost) }}</strong>
                    </div>
                    <div class="profit-line">
                      <span>预计收益</span>
                      <strong>{{
                        financeMoney(row, row.expectedProfit)
                      }}</strong>
                    </div>
                  </div>
                  <span v-else class="muted">-</span>
                </td>
                <td class="address">
                  <div class="address-block">
                    <div v-if="isOnHold(row)" class="subline hold-notice">
                      TikTok暂未开放收件地址
                    </div>
                    <div class="subline">
                      收件人：{{ dash(row.recipientName || row.buyerName) }}
                    </div>
                    <div class="subline">邮编：{{ dash(row.postalCode) }}</div>
                  </div>
                  <div v-if="warehouseOf(row)" class="address-block">
                    <div class="address-label">发货仓</div>
                    <div class="subline">
                      仓库：{{ warehouseOf(row)?.name || '-' }}
                    </div>
                    <div class="subline">
                      州：{{ warehouseOf(row)?.state || '-' }}
                    </div>
                    <div class="subline">
                      邮编：{{ warehouseOf(row)?.postalCode || '-' }}
                    </div>
                  </div>
                </td>
                <td>
                  <div class="row-actions">
                    <button class="link" type="button" @click="openDrawer(row)">
                      详情
                    </button>
                    <button
                      v-if="canDelete(row)"
                      class="link link-danger"
                      type="button"
                      @click="handleDelete(row)"
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination">
          <span>共 {{ total }} 条记录 · {{ pageSize }} 条/页</span>
          <ElPagination
            v-model:current-page="pageNum"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="total"
            background
            layout="sizes, prev, pager, next"
            small
            @current-change="load"
            @size-change="load"
          />
        </div>
      </section>
    </section>

    <aside class="drawer" :class="{ open: drawerOpen }">
      <div class="drawer-head">
        <div>
          <div class="drawer-title">授权订单详情</div>
          <div class="subline">
            {{ dash(currentRow?.platformOrderNo || currentRow?.authOrderNo) }} ·
            店铺名称：{{ displayShopName(currentRow) }}
          </div>
        </div>
        <button class="btn" type="button" @click="closeDrawer">关闭</button>
      </div>

      <div class="drawer-body">
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">订单来源</div>
            <div class="info-value">
              {{ orderSource(currentRow || undefined) }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">平台状态</div>
            <div class="info-value">
              {{ currentRow ? orderStatus(currentRow) : '-' }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">官方面单</div>
            <div class="info-value">
              <span
                v-if="currentRow"
                class="status"
                :class="waybillTone(currentRow)"
              >
                {{ waybillStatus(currentRow) }}
              </span>
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">付款时间</div>
            <div class="info-value">
              {{ formatDateTime(currentRow?.externalPaidAt) }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">客户地址：</div>
            <div class="info-value">{{ customerAddressText(currentRow) }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">发货地址：</div>
            <div class="info-value">{{ warehouseAddressText(currentRow) }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">订单备注</div>
            <div class="info-value">
              {{ dash(currentRow?.customerNote || currentRow?.merchantNote) }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">同步时间</div>
            <div class="info-value">
              {{ formatDateTime(currentRow?.lastSyncedAt) }}
            </div>
          </div>
        </div>

        <template v-if="currentRow && isApiAuthorizedOrder(currentRow)">
          <h3>金额信息</h3>
          <div class="info-grid finance-detail-grid">
            <div class="info-item">
              <div class="info-label">商品实付价</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.productPaidAmount) }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">商品原价</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.productOriginalAmount) }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">商家商品折扣</div>
              <div class="info-value">
                {{
                  financeMoney(
                    currentRow,
                    currentRow.sellerProductDiscountAmount,
                    true,
                  )
                }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">TikTok商品优惠补贴</div>
              <div
                class="info-value"
                v-text="
                  financeMoney(
                    currentRow,
                    currentRow.tiktokProductDiscountAmount,
                  )
                "
              ></div>
            </div>
            <div class="info-item">
              <div class="info-label">顾客实付运费</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.shippingPaidAmount) }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">TikTok运费优惠补贴</div>
              <div
                class="info-value"
                v-text="
                  financeMoney(
                    currentRow,
                    currentRow.tiktokShippingDiscountAmount,
                  )
                "
              ></div>
            </div>
            <div class="info-item finance-highlight">
              <div class="info-label">TikTok优惠补贴合计</div>
              <div
                class="info-value"
                v-text="
                  financeMoney(currentRow, currentRow.tiktokSubsidyAmount)
                "
              ></div>
            </div>
            <div class="info-item">
              <div class="info-label">税务</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.taxAmount) }}
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">顾客实际付款</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.customerPaidAmount) }}
              </div>
            </div>
            <div class="info-item finance-highlight">
              <div class="info-label">估算收入（含顾客实付运费）</div>
              <div
                class="info-value"
                v-text="
                  financeMoney(currentRow, currentRow.estimatedRevenueAmount)
                "
              ></div>
            </div>
            <div class="info-item finance-highlight">
              <div class="info-label">预计结算金额</div>
              <div class="info-value">
                {{
                  financeMoney(currentRow, currentRow.settlementDisplayAmount)
                }}
                <small
                  v-if="currentRow.settlementAmountSource === 'SYSTEM_ESTIMATE'"
                  class="finance-estimate-badge"
                >
                  <span>系统估算</span>
                </small>
              </div>
            </div>
            <div class="info-item">
              <div class="info-label">采购成本</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.purchaseCost) }}
              </div>
            </div>
            <div class="info-item finance-profit">
              <div class="info-label">预计收益</div>
              <div class="info-value">
                {{ financeMoney(currentRow, currentRow.expectedProfit) }}
              </div>
            </div>
          </div>
        </template>

        <h3>商品采购信息</h3>
        <div class="purchase-list">
          <div
            v-for="item in currentRow ? itemLines(currentRow) : []"
            :key="`drawer-${item.index}`"
            class="purchase-row"
          >
            <div class="drawer-product">
              <div class="mini-thumb">
                <img
                  v-if="item.imageUrl"
                  :alt="item.name"
                  class="thumb-image"
                  :src="item.imageUrl"
                />
              </div>
              <strong>{{ item.name }}</strong>
              <span>{{ priceText(item) }}</span>
            </div>
            <label class="field">
              采购数量
              <input :value="item.quantity" />
            </label>
            <label class="field">
              采购 ASIN
              <input :value="item.purchaseAsin || ''" readonly />
            </label>
            <label class="field">
              采购链接
              <input :value="item.purchaseLink || ''" readonly />
            </label>
            <label class="field">
              采购用户备注
              <input :value="item.purchaseUserRemark || ''" readonly />
            </label>
          </div>
        </div>

        <h3>关联订单</h3>
        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">采购订单</div>
            <div class="info-value">
              {{
                currentRow?.purchaseOrderCount
                  ? `已关联 ${currentRow.purchaseOrderCount} 单`
                  : '未关联'
              }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">入库订单</div>
            <div class="info-value">未关联</div>
          </div>
          <div class="info-item">
            <div class="info-label">出库订单</div>
            <div class="info-value">未关联</div>
          </div>
          <div class="info-item">
            <div class="info-label">预上网订单</div>
            <div class="info-value">未关联</div>
          </div>
        </div>
      </div>

      <div class="drawer-foot">
        <button class="btn" type="button" @click="closeDrawer">取消</button>
        <ElTooltip
          v-if="currentRow && currentRow.downstreamEnabled !== 0"
          :disabled="!isOnHold(currentRow)"
          :content="ON_HOLD_BLOCK_REASON"
          placement="top"
        >
          <span>
            <button
              class="btn primary"
              type="button"
              :disabled="isOnHold(currentRow)"
              @click="openPurchaseDialog(currentRow, 'edit')"
            >
              编辑采购信息
            </button>
          </span>
        </ElTooltip>
      </div>
    </aside>

    <ElDialog
      v-model="purchaseDialogOpen"
      :title="isPurchaseEditMode ? '编辑采购信息' : '查看采购信息'"
      width="1080px"
      :close-on-click-modal="false"
    >
      <div v-loading="purchaseDialogLoading">
        <ElEmpty
          v-if="purchaseDialogItems.length === 0"
          :description="
            isPurchaseEditMode
              ? '没有可编辑采购信息的商品（赠品已过滤）'
              : '暂无商品明细'
          "
          :image-size="60"
        />
        <ElTable v-else :data="purchaseDialogItems" border size="small">
          <ElTableColumn label="图片" width="72" align="center">
            <template #default="{ row }">
              <ElImage
                v-if="row.imageUrl"
                :src="row.imageUrl"
                :preview-src-list="[row.imageUrl]"
                fit="cover"
                class="dialog-thumb"
              />
              <div v-else class="dialog-thumb placeholder">图</div>
            </template>
          </ElTableColumn>
          <ElTableColumn label="商品名称" min-width="190">
            <template #default="{ row }">
              <span class="ellipsis" :title="row.productName">{{
                row.productName || '-'
              }}</span>
            </template>
          </ElTableColumn>
          <ElTableColumn label="SKU" width="140">
            <template #default="{ row }">
              {{ row.sku || row.externalSkuId || '-' }}
            </template>
          </ElTableColumn>
          <ElTableColumn label="规格" width="120">
            <template #default="{ row }">{{ row.spec || '-' }}</template>
          </ElTableColumn>
          <ElTableColumn label="数量" width="70" align="center">
            <template #default="{ row }">x{{ row.quantity || 1 }}</template>
          </ElTableColumn>
          <ElTableColumn label="单价" width="100" align="right">
            <template #default="{ row }">
              {{
                priceText({
                  ...row,
                  index: row.lineNo || 1,
                  name: row.productName || '-',
                })
              }}
            </template>
          </ElTableColumn>
          <ElTableColumn min-width="160">
            <template #header>
              <span v-if="isPurchaseEditMode" class="required-asterisk">*</span>
              采购ASIN
            </template>
            <template #default="{ row }">
              <ElInput
                v-if="isPurchaseEditMode"
                v-model="purchaseFormOf(row).purchaseAsin"
                size="small"
                placeholder="必填"
              />
              <span v-else>{{ row.purchaseAsin || '-' }}</span>
            </template>
          </ElTableColumn>
          <ElTableColumn label="采购链接" min-width="180">
            <template #default="{ row }">
              <ElInput
                v-if="isPurchaseEditMode"
                v-model="purchaseFormOf(row).purchaseLink"
                size="small"
                placeholder="采购链接"
              />
              <a
                v-else-if="row.purchaseLink"
                :href="row.purchaseLink"
                target="_blank"
                rel="noopener"
                class="link blue ellipsis"
              >
                {{ row.purchaseLink }}
              </a>
              <span v-else>-</span>
            </template>
          </ElTableColumn>
          <ElTableColumn label="采购用户备注" min-width="180">
            <template #default="{ row }">
              <ElInput
                v-if="isPurchaseEditMode"
                v-model="purchaseFormOf(row).purchaseUserRemark"
                size="small"
                placeholder="采购用户备注"
              />
              <span v-else>{{ row.purchaseUserRemark || '-' }}</span>
            </template>
          </ElTableColumn>
        </ElTable>
      </div>
      <template #footer>
        <ElButton @click="closePurchaseDialog">
          {{ isPurchaseEditMode ? '取消' : '关闭' }}
        </ElButton>
        <ElButton
          v-if="isPurchaseEditMode"
          type="primary"
          :loading="purchaseDialogSubmitting"
          @click="savePurchaseInfo"
        >
          保存
        </ElButton>
      </template>
    </ElDialog>

    <ElDialog
      v-model="waybillDialogOpen"
      :title="waybillDialogNo ? `查看面单 - ${waybillDialogNo}` : '查看面单'"
      width="900px"
      :close-on-click-modal="false"
      destroy-on-close
      @close="closeWaybillDialog"
    >
      <div v-if="waybillDialogUrl" class="waybill-preview-body">
        <ElImage
          v-if="isWaybillImage(waybillDialogUrl)"
          :src="waybillDialogUrl"
          :preview-src-list="[waybillDialogUrl]"
          fit="contain"
          class="waybill-preview-image"
        />
        <iframe
          v-else-if="isWaybillPdf(waybillDialogUrl)"
          :src="waybillDialogUrl"
          class="waybill-preview-frame"
          title="waybill"
        ></iframe>
        <iframe
          v-else
          :src="waybillDialogUrl"
          class="waybill-preview-frame"
          title="waybill"
        ></iframe>
      </div>
      <template #footer>
        <ElButton @click="openWaybillInNewTab">新窗口打开</ElButton>
        <ElButton type="primary" @click="closeWaybillDialog">关闭</ElButton>
      </template>
    </ElDialog>

    <ElDialog
      v-model="manualWaybillDialogOpen"
      title="上传官方面单"
      width="520px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <ElForm label-width="100px" size="default">
        <ElFormItem label="物流单号" required>
          <ElInput
            v-model="manualWaybillNo"
            maxlength="80"
            placeholder="请输入物流单号"
          />
        </ElFormItem>
        <ElFormItem label="面单文件" required>
          <input
            accept="application/pdf,.pdf"
            type="file"
            @change="handleManualWaybillFileChange"
          />
          <div class="upload-tip">
            仅支持 1 个 PDF 文件，提交后由服务器校验完整性
          </div>
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton @click="manualWaybillDialogOpen = false">取消</ElButton>
        <ElButton
          type="primary"
          :loading="manualWaybillSubmitting"
          @click="handleManualWaybillUpload"
        >
          上传
        </ElButton>
      </template>
    </ElDialog>

    <ElDialog
      v-model="buyWaybillDialogOpen"
      title="购买面单"
      width="820px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <ElForm label-width="100px" size="default">
        <ElFormItem label="包裹尺寸">
          <div class="dim-row">
            <ElInputNumber
              v-model="buyWaybillForm.length"
              :min="0"
              :precision="2"
              placeholder="长"
            />
            <span class="dim-x">×</span>
            <ElInputNumber
              v-model="buyWaybillForm.width"
              :min="0"
              :precision="2"
              placeholder="宽"
            />
            <span class="dim-x">×</span>
            <ElInputNumber
              v-model="buyWaybillForm.height"
              :min="0"
              :precision="2"
              placeholder="高"
            />
            <span class="dim-unit">INCH</span>
          </div>
        </ElFormItem>
        <ElFormItem label="包裹重量">
          <div class="dim-row">
            <ElInputNumber
              v-model="buyWaybillForm.weight"
              :min="0"
              :precision="2"
              placeholder="重量"
            />
            <span class="dim-unit">POUND</span>
          </div>
        </ElFormItem>
        <ElFormItem label=" ">
          <ElButton
            type="primary"
            :loading="buyWaybillServicesLoading"
            @click="handleQueryShippingServices"
          >
            查询可用物流服务
          </ElButton>
        </ElFormItem>
        <ElFormItem label="物流商">
          <ElSelect
            :model-value="buyWaybillForm.shippingProviderId"
            placeholder="点上方按钮查询"
            :loading="buyWaybillServicesLoading"
            filterable
            style="width: 100%"
            @update:model-value="onShippingProviderChange"
          >
            <ElOption
              v-for="p in buyWaybillProviders"
              :key="p.id"
              :value="p.id"
              :label="p.name"
            />
          </ElSelect>
        </ElFormItem>
        <ElFormItem label="物流服务">
          <ElSelect
            v-model="buyWaybillForm.shippingServiceId"
            placeholder="先选物流商"
            :loading="buyWaybillServicesLoading"
            filterable
            style="width: 100%"
          >
            <ElOption
              v-for="opt in buyWaybillFilteredServices"
              :key="opt.id"
              :value="opt.id"
              :label="opt.name"
            />
          </ElSelect>
        </ElFormItem>
        <ElFormItem label="预估费用">
          <span class="readonly-text">
            {{ buyWaybillPriceDisplay || '选择物流服务后展示' }}
          </span>
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton @click="buyWaybillDialogOpen = false">取消</ElButton>
        <ElButton
          type="primary"
          :loading="buyWaybillSubmitting"
          @click="handleBuyWaybill"
        >
          确认购买
        </ElButton>
      </template>
    </ElDialog>

    <ElDrawer
      v-model="importDialogOpen"
      title="导入授权订单"
      size="720px"
      :close-on-click-modal="false"
      destroy-on-close
      @closed="resetImportState"
    >
      <div class="import-body">
        <div class="import-toolbar">
          <ElButton
            type="primary"
            plain
            :loading="importTemplateLoading"
            @click="handleDownloadImportTemplate"
          >
            下载导入模板
          </ElButton>
          <ElButton
            type="primary"
            plain
            :loading="importCheckLoading"
            @click="triggerImportFilePick"
          >
            选择导入文件
          </ElButton>
          <input
            ref="importFileInputRef"
            type="file"
            accept=".xlsx"
            class="import-file-hidden"
            @change="onImportFileChange"
          />
          <span v-if="importFile" class="import-file-name">
            已选：{{ importFile.name }} · 共 {{ importTotalRows }} 行
          </span>
        </div>

        <ElTabs v-model="importActiveTab" class="import-tabs">
          <ElTabPane name="success">
            <template #label>
              <span class="custom-tabs-label">
                识别成功
                <ElBadge
                  v-if="importSuccessRows.length > 0"
                  class="mark"
                  :value="importSuccessRows.length"
                  :max="importSuccessRows.length + 2"
                />
              </span>
            </template>
            <div class="import-grid-wrap">
              <ImportSuccessGrid />
            </div>
          </ElTabPane>
          <ElTabPane name="fail">
            <template #label>
              <span class="custom-tabs-label">
                识别失败
                <ElBadge
                  v-if="importFailRows.length > 0"
                  class="mark"
                  :value="importFailRows.length"
                  :max="importFailRows.length + 2"
                />
              </span>
            </template>
            <div class="import-grid-wrap">
              <ImportFailGrid>
                <template #errorsCell="{ row }">
                  <div class="import-error-tags">
                    <ElTag
                      v-for="(err, idx) in row.errors || []"
                      :key="idx"
                      type="danger"
                      size="small"
                      effect="light"
                      class="import-error-tag"
                    >
                      {{ err }}
                    </ElTag>
                  </div>
                </template>
              </ImportFailGrid>
            </div>
          </ElTabPane>
        </ElTabs>

        <div v-if="importFinalResult" class="import-result">
          导入完成 · 新增
          <b>{{ importFinalResult.createdCount }}</b>
          单
          <span v-if="importFinalResult.skippedExistsCount">
            · 跳过已存在 {{ importFinalResult.skippedExistsCount }} 单
          </span>
        </div>
      </div>
      <template #footer>
        <ElButton @click="importDialogOpen = false">关闭</ElButton>
        <ElButton
          type="primary"
          :loading="importSubmitting"
          @click="submitImport"
        >
          提交导入
        </ElButton>
      </template>
    </ElDrawer>

    <PurchaseAddDrawer />

    <InboundOrderCreateSplitDialog
      v-model="inboundDialogOpen"
      mode="transit"
      :preset-authorized-order="inboundPresetAuth"
      :preset-purchase-order-ids="inboundPresetPurchaseIds"
      @created="onInboundCreated"
    />

    <InboundOrderDetailDialog
      v-model="inboundDetailVisible"
      :row="inboundDetailRow as any"
    />

    <OutboundOrderCreateDialog
      v-model="outboundDialogOpen"
      :preset-authorized-order="outboundPresetAuth"
      @success="onOutboundCreated"
    />

    <PreOnlineOrderCreateDialog
      v-model="preOnlineDialogOpen"
      :preset-authorized-order="preOnlinePresetAuth"
      @created="onPreOnlineCreated"
    />

    <PreOnlineOrderDetailDialog
      v-model="preOnlineDetailVisible"
      :row="preOnlineDetailRow"
    />
  </div>
</template>

<style scoped>
@media (max-width: 1180px) {
  .query-bar {
    grid-template-columns: 1fr 1fr;
  }

  .query-actions {
    justify-content: flex-start;
  }
}

.auth-order-workbench {
  --bg: #f4f6f8;
  --panel: #fff;
  --line: #dfe5ee;
  --line-soft: #edf1f6;
  --text: #172033;
  --muted: #667085;
  --subtle: #98a2b3;
  --orange: #ff7a00;
  --orange-soft: #fff3e7;
  --blue: #2563eb;
  --blue-soft: #eaf1ff;
  --green: #15803d;
  --green-soft: #e8f7ee;
  --red: #dc2626;
  --red-soft: #feecec;
  --amber: #b45309;
  --amber-soft: #fff7df;
  --shadow: 0 10px 28px rgb(20 31 48 / 8%);
  --item-row-height: 116px;

  min-height: calc(100vh - 84px);
  font-size: 14px;
  color: var(--text);
  background: var(--bg);
}

.sync-progress-mask {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(15 23 42 / 34%);
}

.sync-progress-panel {
  width: min(420px, calc(100vw - 40px));
  padding: 24px 26px;
  color: var(--text);
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  box-shadow: 0 20px 48px rgb(15 23 42 / 18%);
}

.sync-progress-title {
  font-size: 17px;
  font-weight: 700;
}

.sync-progress-text {
  min-height: 22px;
  margin-top: 10px;
  font-size: 13px;
  color: var(--muted);
}

.sync-progress-track {
  height: 8px;
  margin-top: 18px;
  overflow: hidden;
  background: #edf1f6;
  border-radius: 999px;
}

.sync-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #2563eb 0%, #0f766e 100%);
  border-radius: inherit;
  transition: width 0.35s ease;
}

.sync-progress-percent {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  text-align: right;
}

.auth-order-workbench * {
  box-sizing: border-box;
}

.auth-order-workbench button,
.auth-order-workbench input,
.auth-order-workbench select {
  font: inherit;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 20px;
  background: var(--panel);
  border-bottom: 1px solid var(--line);
}

.title-block {
  display: flex;
  gap: 12px;
  align-items: baseline;
  min-width: 0;
}

.title-block h1 {
  margin: 0;
  font-size: 20px;
  line-height: 1;
}

.breadcrumb,
.header-actions,
.selection-text,
.subline {
  font-size: 12px;
  color: var(--muted);
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.content {
  min-width: 0;
  padding: 16px;
}

.auth-status-tabs {
  background: var(--panel);
}

.query-bar {
  display: grid;
  grid-template-columns: minmax(360px, 1.8fr) 180px 180px 180px auto;
  gap: 10px;
  align-items: end;
  padding: 14px;
  margin-bottom: 12px;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 8px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
}

.field input,
.field select {
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  color: var(--text);
  outline: none;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
}

.field input:focus,
.field select:focus,
.mini-input:focus {
  border-color: #ffb15c;
  box-shadow: 0 0 0 3px rgb(255 122 0 / 12%);
}

.query-actions,
.toolbar-left,
.row-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  font-weight: 800;
  color: #344054;
  text-decoration: none;
  cursor: pointer;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
}

.btn.primary {
  color: #fff;
  background: var(--orange);
  border-color: var(--orange);
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.table-card {
  overflow: hidden;
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: 8px;
  box-shadow: var(--shadow);
}

.table-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
}

.toolbar-select {
  min-width: 110px;
  height: 34px;
  padding: 0 10px;
  color: var(--text);
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
}

.table-wrap {
  max-height: calc(100vh - 300px);
  overflow: auto;
}

table {
  width: 100%;
  min-width: 1910px;
  border-spacing: 0;
  border-collapse: separate;
}

th,
td {
  padding: 12px;
  vertical-align: middle;
  background: #fff;
  border-bottom: 1px solid var(--line-soft);
}

th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 42px;
  font-size: 13px;
  font-weight: 900;
  color: #475467;
  text-align: left;
  background: #f8fafc;
}

tbody tr:hover td {
  background: #fffaf5;
}

.check-col {
  width: 44px;
  text-align: center;
}

.color-tag-col {
  width: 78px;
}

.color-tag-cell {
  display: flex;
  gap: 6px;
  align-items: center;
}

.color-tag-dot {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border: 1px solid rgb(0 0 0 / 12%);
  border-radius: 50%;
}

.color-tag-dot.empty {
  background: transparent;
  border-style: dashed;
}

.color-tag-select {
  width: 52px;
  height: 24px;
  padding: 0 4px;
  color: #606266;
  outline: none;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.order-col {
  width: 210px;
}

.product-col {
  width: 420px;
}

.progress-cell {
  min-width: 430px;
}

.finance-col {
  min-width: 230px;
  vertical-align: middle;
}

.finance-summary {
  display: grid;
  gap: 6px;
  font-size: 12px;
  line-height: 1.45;
}

.finance-summary > div {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  white-space: nowrap;
}

.finance-summary span {
  color: var(--muted);
}

.finance-summary strong {
  color: var(--text);
}

.finance-summary .finance-settlement-line strong {
  color: #2563eb;
}

.finance-estimate-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
  margin-left: 4px;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  color: #92400e !important;
  background: #fef3c7;
  border-radius: 999px;
}

.finance-summary .profit-line strong,
.finance-profit .info-value {
  color: #059669;
}

.finance-highlight {
  background: #fffaf5;
}

.finance-detail-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.order-no {
  margin-bottom: 6px;
  font-weight: 900;
  line-height: 1.4;
  color: #009f86;
}

.shop-name {
  margin-top: 4px;
  font-weight: 900;
  line-height: 1.5;
  color: var(--text);
}

.product-list,
.purchase-mini-list {
  display: grid;
  gap: 0;
  min-width: 0;
}

.product-mini {
  position: relative;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  height: var(--item-row-height);
  padding: 10px 0;
  overflow: visible;
  border-bottom: 1px solid var(--line-soft);
}

.product-mini:last-child,
.purchase-mini:last-child {
  border-bottom: 0;
}

.mini-thumb {
  position: relative;
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  overflow: visible;
  background:
    linear-gradient(135deg, rgb(37 99 235 / 14%), rgb(255 122 0 / 20%)), #fff;
  border: 1px solid var(--line);
  border-radius: 5px;
}

.mini-thumb .thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.product-image-preview.has-image {
  cursor: zoom-in;
}

.image-hover-preview {
  position: absolute;
  top: 50%;
  left: calc(100% + 10px);
  z-index: 30;
  visibility: hidden;
  width: 168px;
  max-width: none;
  height: 168px;
  padding: 6px;
  pointer-events: none;
  object-fit: contain;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 16px 38px rgb(15 23 42 / 24%);
  opacity: 0;
  transform: translateY(-50%) scale(0.96);
  transition:
    opacity 0.16s ease,
    transform 0.16s ease,
    visibility 0.16s ease;
}

.product-image-preview:hover,
.product-image-preview:focus-within {
  z-index: 30;
}

.product-image-preview:hover .image-hover-preview,
.product-image-preview:focus-within .image-hover-preview {
  visibility: visible;
  opacity: 1;
  transform: translateY(-50%) scale(1);
}

.product-mini-body {
  min-width: 0;
}

.mini-title {
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 900;
  white-space: nowrap;
}

.gift-tag {
  display: inline-block;
  padding: 0 6px;
  margin-right: 4px;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  vertical-align: middle;
  color: #fff;
  background: #f56c6c;
  border-radius: 3px;
}

.product-meta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 2px 10px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted);
}

.product-meta span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.purchase-mini {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
  height: var(--item-row-height);
  padding: 10px 0;
  overflow: hidden;
  border-bottom: 1px solid var(--line-soft);
}

.purchase-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted);
}

.purchase-display span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.purchase-info-cell {
  position: relative;
  padding-top: 28px !important;
}

.cell-action-bar {
  position: absolute;
  top: 4px;
  right: 8px;
  z-index: 1;
  display: flex;
  gap: 4px;
  align-items: center;
}

.cell-action-bar :deep(.el-button.is-link) {
  height: auto;
  min-height: 18px;
  padding: 0 4px;
}

.mini-form {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 6px;
  min-width: 0;
}

.mini-form label {
  display: grid;
  gap: 3px;
  font-size: 11px;
  font-weight: 800;
  color: var(--muted);
}

.mini-input {
  width: 100%;
  min-width: 0;
  height: 28px;
  padding: 0 7px;
  font-size: 12px;
  outline: none;
  border: 1px solid var(--line);
  border-radius: 5px;
}

.mini-index {
  font-size: 12px;
  font-weight: 900;
  color: var(--muted);
}

.tag-list,
.label-line {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.pill,
.status {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  border-radius: 999px;
}

.pill {
  color: #475467;
  background: #f2f4f7;
}

.status.warn {
  color: var(--amber);
  background: var(--amber-soft);
}

.status.ok {
  color: var(--green);
  background: var(--green-soft);
}

.status.bad {
  color: var(--red);
  background: var(--red-soft);
}

.status.info {
  color: var(--blue);
  background: var(--blue-soft);
}

.label-cell {
  display: grid;
  gap: 5px;
  min-width: 240px;
}

/* 物流单号 22 位（如 9200190393385408150165）不换行 */
.label-cell .subline {
  white-space: nowrap;
}

.link {
  padding: 0;
  font-weight: 900;
  color: var(--orange);
  text-decoration: none;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.link.blue {
  color: var(--blue);
}

.inline-action {
  height: auto;
  padding: 0;
  font-weight: 900;
  vertical-align: baseline;
}

.assoc {
  display: grid;
  gap: 5px;
  min-width: 280px;
  font-size: 12px;
  line-height: 1.45;
}

.assoc-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  align-items: center;
  white-space: nowrap;
}

.assoc-label {
  flex-shrink: 0;
  min-width: 44px;
  color: var(--muted);
}

.address {
  width: 280px;
  line-height: 1.6;
}

.address-block + .address-block {
  padding-top: 6px;
  margin-top: 6px;
  border-top: 1px dashed var(--border, #eee);
}

.address-label {
  font-size: 11px;
  color: var(--muted);
}

.dim-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dim-row-grow {
  width: 100%;
}

.dim-x {
  font-size: 12px;
  color: var(--muted);
}

.dim-unit {
  margin-left: 4px;
  font-size: 12px;
  color: var(--muted);
}

.readonly-text {
  font-size: 14px;
  color: var(--el-text-color-regular, #606266);
}

.import-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}

.import-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.import-file-hidden {
  display: none;
}

.import-file-name {
  margin-left: 4px;
  font-size: 12px;
  color: var(--el-color-success, #67c23a);
}

.import-tip {
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--muted);
  background: var(--fill-color-light, #fafafa);
  border-radius: 4px;
}

.import-file-input {
  display: block;
}

.import-result {
  padding: 8px 10px;
  font-size: 13px;
  background: var(--fill-color-light, #fafafa);
  border-radius: 4px;
}

.import-failures {
  max-height: 220px;
  margin-top: 8px;
  overflow-y: auto;
}

.import-failure-row {
  display: flex;
  gap: 8px;
  padding: 2px 0;
  font-size: 12px;
  border-top: 1px dashed var(--border, #eee);
}

.import-row-no {
  flex-shrink: 0;
  width: 50px;
  color: var(--muted);
}

.import-row-po {
  flex-shrink: 0;
  width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.import-row-err {
  color: var(--el-color-danger, #f56c6c);
}

.import-failure-more {
  margin-top: 6px;
  font-size: 12px;
  color: var(--muted);
}

.import-tabs {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  margin-top: 4px;
}

.import-tabs :deep(.el-tabs__content) {
  flex: 1;
  min-height: 0;
}

.import-tabs :deep(.el-tab-pane) {
  height: 100%;
}

.import-grid-wrap {
  height: calc(100vh - 320px);
  overflow: hidden;
}

.import-error-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 0;
  line-height: 1.4;
}

.import-error-tag {
  max-width: 100%;
  height: auto;
  padding: 2px 6px;
  line-height: 1.3;
  white-space: normal;
}

:deep(.vxe-cell.import-error-col),
:deep(.vxe-body--column.import-error-col .vxe-cell) {
  height: auto !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: normal !important;
}

.custom-tabs-label {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.empty-cell {
  height: 160px;
  color: var(--muted);
  text-align: center;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--muted);
  border-top: 1px solid var(--line);
}

.drawer {
  position: fixed;
  inset: 0 0 0 auto;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  width: min(760px, 100vw);
  background: #fff;
  box-shadow: -18px 0 44px rgb(15 23 42 / 20%);
  transform: translateX(104%);
  transition: transform 0.18s ease;
}

.drawer.open {
  transform: translateX(0);
}

.drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 68px;
  padding: 0 18px;
  border-bottom: 1px solid var(--line);
}

.drawer-title {
  margin-bottom: 4px;
  font-size: 18px;
  font-weight: 900;
}

.drawer-body {
  padding: 16px 18px 86px;
  overflow: auto;
}

.drawer-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--line);
}

.drawer-tabs button {
  height: 38px;
  font-weight: 900;
  color: var(--muted);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
}

.drawer-tabs button.active {
  color: var(--orange);
  border-bottom-color: var(--orange);
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}

.info-item {
  min-height: 64px;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.info-label {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 800;
  color: var(--muted);
}

.info-value {
  font-weight: 900;
  line-height: 1.45;
}

.purchase-list {
  display: grid;
  gap: 10px;
}

.purchase-row {
  display: grid;
  grid-template-columns:
    minmax(150px, 1.3fr) 90px minmax(112px, 1fr) minmax(150px, 1.2fr)
    minmax(140px, 1fr);
  gap: 10px;
  align-items: end;
  padding: 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
}

.drawer-product {
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 2px 8px;
  align-items: center;
  min-width: 0;
}

.drawer-product .mini-thumb {
  grid-row: 1 / span 2;
}

.drawer-product strong,
.drawer-product span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-product span {
  font-size: 12px;
  font-weight: 800;
  color: var(--muted);
}

.required-asterisk {
  margin-right: 2px;
  color: var(--el-color-danger);
}

.dialog-thumb {
  width: 48px;
  height: 48px;
  border-radius: 4px;
}

.dialog-thumb.placeholder {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  border: 1px dashed #c0c4cc;
}

.ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.drawer-foot {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  height: 68px;
  padding: 0 18px;
  background: #fff;
  border-top: 1px solid var(--line);
}

.waybill-preview-body {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
  max-height: 70vh;
  background: #f5f7fa;
  border-radius: 4px;
}

.waybill-preview-frame {
  width: 100%;
  height: 70vh;
  background: #fff;
  border: 0;
}

.waybill-preview-image {
  max-width: 100%;
  max-height: 70vh;
}
</style>
